import React, { useState, useEffect, useRef, useContext } from "react";
import { motion, AnimatePresence } from "framer-motion";
import bannerBack from "../../images/banner_background.png";
import logoWhite from "../../images/logo_white.png";
import { DateTime } from "luxon";
import Screening from "./Screening";
import { UrlContext } from "../../contexts/UrlContext";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import TimezoneSelect from "react-timezone-select";
import {
  format,
  addHours,
  parse,
  differenceInMonths,
  startOfMonth,
  endOfMonth,
} from "date-fns";
import { v4 as uuidv4 } from "uuid";
import reactGa from "react-ga4";
import Swal from "sweetalert2";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { useNavigate } from "react-router-dom";
import ProfilePicture from "./ProfilePicture";
import expertDefaultImg from "../../images/expert-default.png";
import industryLogo from "../../images/default_industry.png";
import DemoProfile from "./DemoProfile";

function stableSerialize(value) {
  if (value === null || typeof value !== "object") {
    return JSON.stringify(value);
  }

  if (Array.isArray(value)) {
    return `[${value.map((item) => stableSerialize(item)).join(",")}]`;
  }

  const keys = Object.keys(value).sort();
  return `{${keys
    .map((key) => `${JSON.stringify(key)}:${stableSerialize(value[key])}`)
    .join(",")}}`;
}

function getMobileTimelineItems(activeStep, labels) {
  const reviewIndex = labels.length - 1;
  const lastApplicationStageIndex = reviewIndex - 1;

  const stageItem = (index) => ({
    kind: "stage",
    key: `stage-${index}`,
    index,
    label: labels[index],
  });

  const counterItem = (count, side, targetIndex = null) => ({
    kind: "counter",
    key: `${side}-counter-${activeStep}-${count}`,
    count,
    side,
    targetIndex,
  });

  // The first two stages stay explicit. Everything between examples and
  // review is collapsed into one counter, producing four visible items.
  if (activeStep <= 1) {
    return [
      stageItem(0),
      stageItem(1),
      counterItem(lastApplicationStageIndex - 1, "future"),
      stageItem(reviewIndex),
    ];
  }

  // Keep the final review state at four visible timeline items as well:
  // hidden earlier stages, Rate, Consent, Review.
  if (activeStep === reviewIndex) {
    return [
      counterItem(4, "past", 3),
      stageItem(4),
      stageItem(5),
      stageItem(reviewIndex),
    ];
  }

  const items = [];
  const hiddenPreviousCount = activeStep - 1;
  const hiddenFutureCount = lastApplicationStageIndex - activeStep;

  if (hiddenPreviousCount > 0) {
    items.push(
      counterItem(
        hiddenPreviousCount,
        "past",
        Math.max(0, activeStep - 2)
      )
    );
  }

  items.push(stageItem(activeStep - 1));
  items.push(stageItem(activeStep));

  if (hiddenFutureCount > 0) {
    items.push(counterItem(hiddenFutureCount, "future"));
  }

  if (activeStep !== reviewIndex) {
    items.push(stageItem(reviewIndex));
  }

  return items;
}

export default function Application({
  project,
  selfApplied,
  expertID,
  updateProjectStatus,
  onClose,
  embeddedWizardMode = false,
}) {
  const { API_URL } = useContext(UrlContext);
  const projectID =
    project?.id ??
    project?.ID ??
    project?.projectID ??
    project?.project_id ??
    null;
  const [isMobileViewport, setIsMobileViewport] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < 640 : false
  );

  useEffect(() => {
    const updateMobileViewport = () => {
      setIsMobileViewport(window.innerWidth < 640);
    };

    updateMobileViewport();
    window.addEventListener("resize", updateMobileViewport);
    return () => window.removeEventListener("resize", updateMobileViewport);
  }, []);

  const effectiveEmbeddedWizardMode =
    embeddedWizardMode || isMobileViewport;
  const [question1, setQuestion1] = useState("");
  const [question2, setQuestion2] = useState([""]);
  const QUESTION2_LIMIT = 4000;
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [overLimit, setOverLimit] = useState(false);
  const editableRef = useRef(null);
  const [screenQuestions, setScreenQuestions] = useState([]);
  const [queAnswers, setQueAnswers] = useState([]);
  const [showQuestions, setShowQuestions] = useState(false);

  const [availability, setAvailability] = useState([]);
  const [availabilityForm, setAvailabilityForm] = useState(false);
  const [fullyAvailable, setFullyAvailable] = useState(true);
  const [availabilityComment, setAvailabilityComment] = useState("Fully available");
  const [hourlyRateCurrency, setHourlyRateCurrency] = useState("EUR");
  const [dailyRateCurrency, setDailyRateCurrency] = useState("EUR");
  const [hourlyRate, setHourlyRate] = useState("");
  const [dailyRate, setDailyRate] = useState("");
  const [recordConsent, setRecordConsent] = useState(true);
  const [timezone, setTimezone] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone
  );
  const currencies = ["EUR", "USD", "GBP", "CHF", "AUD", "AED", "INR"];
  function generateUniqueId() {
    return uuidv4();
  }

  // Currency threshold daily
  const DAILY_THRESHOLD = {
    EUR: 800,
    USD: 1000,
    GBP: 700,
    CHF: 800,
    AED: 3000,
    AUD: 1350,
    INR: 16500,
  };

  // Currency threshold hourly
  const HOURLY_THRESHOLD = {
    USD: 300,
    EUR: 275,
    GBP: 240,
    CHF: 265,
    AED: 1100,
    AUD: 460,
    INR: 12500,
  };

  const getDailyThreshold = (rate) => {
    return DAILY_THRESHOLD[rate] ?? 800; // fallback
  };

  const getHourlyThreshold = (rate) => {
    return HOURLY_THRESHOLD[rate] ?? 300; // fallback
  };

  // Save updated data into form state
  const [formData, setFormData] = useState({
    question1: question1,
    question2: question2,
    availability: availability,
    availabilityForm: availabilityForm,
    fullyAvailable: fullyAvailable,
    availabilityComment: availabilityComment,
    hourlyRateCurrency: hourlyRateCurrency,
    dailyRateCurrency: dailyRateCurrency,
    hourlyRate: hourlyRate,
    dailyRate: dailyRate,
    recordConsent: recordConsent,
    education: "",
    experience: [],
    position: 'Independent Consultant',
    location: '',
    language: '',
    timezone: timezone,
  });

  // steps states
  const [isWritting, setIsWritting] = useState("");
  const [step, setStep] = useState(0);
  const totalSteps = 7;
  const [draftLoading, setDraftLoading] = useState(true);
  const [stageSaving, setStageSaving] = useState(false);
  const [draftRestored, setDraftRestored] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [highestCompletedStage, setHighestCompletedStage] = useState(-1);
  const lastSavedDraftSignatureRef = useRef("");

  // Steps validator (project details, summery, relevant project, timeslots, availability, rate, consent)
  const stepValidators = {
    0: () => formData.question1.trim().length > 0 || isWritting.trim().length > 0,
    1: () => true,
    2: () => true,
    3: () => true,
    4: () => {
      const isExpert = project?.cons_exp === "exp";
      const currency = isExpert ? formData.hourlyRateCurrency : formData.dailyRateCurrency;
      const rate = isExpert ? formData.hourlyRate : formData.dailyRate;
      if (!currency || currency.trim() === "") return false;
      if (!/^\d+$/.test(rate.toString().trim())) return false;
      return true;
    },
    5: () => true,
    6: () => true,
  };

  // next and previous step with validation
  const canProceed = stepValidators[step]();

  const buildDraftFormData = (
    completedStage = highestCompletedStage
  ) => ({
    ...formData,
    question1,
    question2,
    availability,
    fullyAvailable,
    availabilityComment,
    hourlyRateCurrency,
    dailyRateCurrency,
    hourlyRate,
    dailyRate,
    recordConsent,
    timezone,
    screeningAnswers: queAnswers,
    _wizardProgress: {
      highestCompletedStage: completedStage,
    },
  });

  const buildDraftPayload = ({
    activeStage = step,
    completedStage = highestCompletedStage,
  } = {}) => ({
    projectID,
    expertID,
    isSelfApplied: Boolean(selfApplied),
    currentStage: activeStage,
    formData: buildDraftFormData(completedStage),
  });

  const saveStage = async ({
    activeStage = step,
    completedStage = highestCompletedStage,
  } = {}) => {
    if (!projectID) {
      Swal.fire({
        icon: "error",
        title: "Could not save progress",
        text: "Project ID is missing from the application data.",
        confirmButtonColor: "#365561",
      });
      return false;
    }

    const payload = buildDraftPayload({
      activeStage,
      completedStage,
    });

    // currentStage is intentionally excluded from this signature.
    // Merely moving between already-completed stages should not cause a write.
    const nextSignature = stableSerialize(payload.formData);

    if (
      lastSavedDraftSignatureRef.current &&
      nextSignature === lastSavedDraftSignatureRef.current
    ) {
      return true;
    }

    const tokenObj = localStorage.getItem("eph-token");
    const token = JSON.parse(tokenObj).value || localStorage.getItem("AccessToken");
    setStageSaving(true);

    try {
      const response = await fetch(`${API_URL}save-consultant-application-stage`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const rawResponse = await response.text();
      let data = {};

      try {
        data = rawResponse ? JSON.parse(rawResponse) : {};
      } catch {
        data = {};
      }

      if (!response.ok || data.status !== "success") {
        throw new Error(
          data.message ||
            data.text ||
            rawResponse ||
            `Could not save this stage (HTTP ${response.status}).`
        );
      }

      lastSavedDraftSignatureRef.current = nextSignature;
      setLastSavedAt(new Date());
      return true;
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Could not save progress",
        text: error.message,
        confirmButtonColor: "#365561",
      });
      return false;
    } finally {
      setStageSaving(false);
    }
  };

  const nextStep = async () => {
    if (!canProceed) {
      setHasSubmitted(true);
      return;
    }

    const next = Math.min(step + 1, totalSteps - 1);
    const nextHighestCompletedStage = Math.max(
      highestCompletedStage,
      step
    );

    const saved = await saveStage({
      activeStage: next,
      completedStage: nextHighestCompletedStage,
    });

    if (saved) {
      setHasSubmitted(false);
      setHighestCompletedStage(nextHighestCompletedStage);
      setStep(next);
    }
  };

  const prevStep = async () => {
    const previous = Math.max(step - 1, 0);

    const saved = await saveStage({
      activeStage: previous,
      completedStage: highestCompletedStage,
    });

    if (saved) {
      setHasSubmitted(false);
      setStep(previous);
    }
  };

  useEffect(() => {
    if (!projectID) {
      setDraftLoading(false);
      return;
    }

    const controller = new AbortController();
    const tokenObj = localStorage.getItem("eph-token");
    const token = JSON.parse(tokenObj).value || localStorage.getItem("AccessToken");

    const loadDraft = async () => {
      setDraftLoading(true);
      try {
        const response = await fetch(`${API_URL}get-consultant-application-draft`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            projectID,
            expertID,
            isSelfApplied: Boolean(selfApplied),
          }),
          signal: controller.signal,
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok || data.status !== "success" || !data.draft) return;

        const saved = data.draft.formData || {};
        const {
          _wizardProgress,
          ...savedFormValues
        } = saved;

        const savedQuestion2 =
          Array.isArray(saved.question2) && saved.question2.length
            ? saved.question2
            : [""];
        const savedAvailability = Array.isArray(saved.availability)
          ? saved.availability
          : [];
        const restoredActiveStage = Math.min(
          Number(data.draft.currentStage) || 0,
          totalSteps - 1
        );

        const storedCompletedStage = Number(
          _wizardProgress?.highestCompletedStage
        );
        const restoredCompletedStage = Number.isFinite(
          storedCompletedStage
        )
          ? Math.max(
              -1,
              Math.min(storedCompletedStage, totalSteps - 2)
            )
          : Math.max(-1, restoredActiveStage - 1);

        const restoredDraftFormData = {
          ...savedFormValues,
          question1: saved.question1 || "",
          question2: savedQuestion2,
          availability: savedAvailability,
          fullyAvailable: saved.fullyAvailable ?? true,
          availabilityComment:
            saved.availabilityComment || "Fully available",
          hourlyRateCurrency: saved.hourlyRateCurrency || "EUR",
          dailyRateCurrency: saved.dailyRateCurrency || "EUR",
          hourlyRate: saved.hourlyRate ?? "",
          dailyRate: saved.dailyRate ?? "",
          recordConsent: saved.recordConsent ?? true,
          timezone:
            saved.timezone ||
            Intl.DateTimeFormat().resolvedOptions().timeZone,
          screeningAnswers: Array.isArray(saved.screeningAnswers)
            ? saved.screeningAnswers
            : [],
          _wizardProgress: {
            highestCompletedStage: restoredCompletedStage,
          },
        };

        setQuestion1(restoredDraftFormData.question1);
        setQuestion2(savedQuestion2);
        setAvailability(savedAvailability);
        setFullyAvailable(restoredDraftFormData.fullyAvailable);
        setAvailabilityComment(
          restoredDraftFormData.availabilityComment
        );
        setHourlyRateCurrency(
          restoredDraftFormData.hourlyRateCurrency
        );
        setDailyRateCurrency(
          restoredDraftFormData.dailyRateCurrency
        );
        setHourlyRate(restoredDraftFormData.hourlyRate);
        setDailyRate(restoredDraftFormData.dailyRate);
        setRecordConsent(restoredDraftFormData.recordConsent);
        setTimezone(restoredDraftFormData.timezone);
        setQueAnswers(restoredDraftFormData.screeningAnswers);
        setFormData((current) => ({
          ...current,
          ...savedFormValues,
          question1: restoredDraftFormData.question1,
          question2: savedQuestion2,
          availability: savedAvailability,
        }));
        setStep(restoredActiveStage);
        setHighestCompletedStage(restoredCompletedStage);
        lastSavedDraftSignatureRef.current = stableSerialize(
          restoredDraftFormData
        );
        setDraftRestored(true);
        setLastSavedAt(
          data.draft.updatedAt
            ? new Date(data.draft.updatedAt)
            : null
        );
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Could not restore application draft:", error);
        }
      } finally {
        setDraftLoading(false);
      }
    };

    loadDraft();
    return () => controller.abort();
  }, [API_URL, projectID, expertID, selfApplied]);

  // Rate negotiation
  const [negotiatedRate, setNegotiatedRate] = useState(null);
  const [showNegotiation, setShowNegotiation] = useState(false);

  // Block body scroll when modal is open
  useEffect(() => {
    document.body.classList.add("overflow-hidden");
    return () => {
      document.body.classList.remove("overflow-hidden");
    };
  }, []);

  // Set focus on the editable div when the component mounts
  useEffect(() => {
    if (editableRef.current) {
      editableRef.current.innerText = question1;
    }
  }, []);

  // Getting the screening questions
  useEffect(() => {
    if (!project?.id) return;

    const tokenObj = localStorage.getItem("eph-token");
    const token = JSON.parse(tokenObj).value || localStorage.getItem("AccessToken");
    const url = `${API_URL}get-profile-screen-questions/${project.id}`;

    fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  })
    .then((res) => res.json())
    .then((data) => {
      if (data.status === "success") {
        setShowQuestions(true);
        setScreenQuestions(
          data.questions.length === 1 && !data.questions[0].question ? [] : data.questions
        );
      } else {
        console.log("error");
      }
    })
    .catch(console.log);
}, [API_URL, project?.id]);

  // formatting the start date
  const formatStartDate = (dateStr) => {
    const date = DateTime.fromRFC2822(dateStr);
    if (!date.isValid) return "N/A";
    return `${date.toFormat("LLLL d, yyyy")}`;
  };

  // Get employment mode
  const getEmploymentMode = (type) => {
    if (!type) return "N/A";
    const lower = type.toLowerCase();
    const hasRemote = lower.includes("remote");
    const hasOnsite = lower.includes("onsite");
    if (hasRemote && hasOnsite) return "Hybrid";
    if (hasRemote) return "Remote";
    if (hasOnsite) return "On-Site";
    return "N/A";
  };

  // Handle input for question 1 to limit to 500 characters
  // const handleInput = (e) => {
  //   if (e.target.innerText.length <= 500) {
  //     setQuestion1(e.target.innerText);
  //   } else {
  //     e.target.innerText = question1;
  //     e.preventDefault();
  //   }
  // }
  const handleInput = (e) => {
    setHasSubmitted(false);
    const text = e.target.innerText;
    setIsWritting(text);

    if (text.length <= 500) {
      setQuestion1(text);
      setOverLimit(false);
    } else {
      // Prevent further input by restoring previous state
      const trimmed = text.slice(0, 500);
      e.target.innerText = trimmed;

      // Move caret to end (after resetting innerText)
      const range = document.createRange();
      const sel = window.getSelection();
      range.selectNodeContents(e.target);
      range.collapse(false);
      sel.removeAllRanges();
      sel.addRange(range);

      setQuestion1(trimmed);
      setOverLimit(true);
    }
  };

  const handlePaste = (e) => {
    setHasSubmitted(false);
    e.preventDefault();
    const pastedText = e.clipboardData.getData("text/plain");
    const currentText = editableRef.current.innerText;
    const spaceLeft = 500 - currentText.length;

    const textToInsert = pastedText.slice(0, spaceLeft);
    document.execCommand("insertText", false, textToInsert);

    const finalText = (currentText + textToInsert).slice(0, 500);
    setQuestion1(finalText);
    setOverLimit(currentText.length + pastedText.length > 500);
  };

  function groupConsecutiveTimestamps(timestamps = []) {
    const sortedTimestamps = [...timestamps]
      .map(Number)
      .filter(Number.isFinite)
      .sort((a, b) => a - b);

    const groups = [];
    let group = [];

    const getGroupEndTimestamp = (currentGroup) => {
      const lastSlotTimestamp =
        currentGroup[currentGroup.length - 1];
      const lastSlotDate = new Date(
        lastSlotTimestamp * 1000
      );

      // A selected 23:00 slot represents availability until
      // 23:59 rather than midnight of the following day.
      return (
        lastSlotTimestamp +
        (lastSlotDate.getUTCHours() === 23
          ? 3599
          : 3600)
      );
    };

    for (let i = 0; i < sortedTimestamps.length; i++) {
      if (
        group.length === 0 ||
        sortedTimestamps[i] -
          group[group.length - 1] ===
          3600
      ) {
        group.push(sortedTimestamps[i]);
      } else {
        groups.push([
          group[0],
          getGroupEndTimestamp(group),
        ]);
        group = [sortedTimestamps[i]];
      }
    }

    if (group.length > 0) {
      groups.push([
        group[0],
        getGroupEndTimestamp(group),
      ]);
    }

    return groups;
  }

  function formatDate(timestamp) {
    const encodedDate = new Date(Number(timestamp) * 1000);

    if (Number.isNaN(encodedDate.getTime())) {
      return "Invalid date";
    }

    const weekday = encodedDate.toLocaleDateString("en-US", {
      weekday: "short",
      timeZone: "UTC",
    });
    const month = encodedDate.toLocaleDateString("en-US", {
      month: "short",
      timeZone: "UTC",
    });
    const day = encodedDate.getUTCDate();
    const hour = String(encodedDate.getUTCHours()).padStart(2, "0");
    const minute = String(encodedDate.getUTCMinutes()).padStart(2, "0");

    return `${weekday}, ${month} ${day}, ${hour}:${minute}`;
  }

  // Handle submission of the form
  // Check if question1 is empty and set hasSubmitted to true
  const submitApplication = async () => {
    setHasSubmitted(true);
    if (
      !formData.question1.trim() ||
      formData.question1.replace(/\u00A0|\u200B|\n/g, "").trim().length === 0
    )
      return;

    const draftSaved = await saveStage({
      activeStage: totalSteps - 1,
      completedStage: highestCompletedStage,
    });
    if (!draftSaved) return;

    const tokenObj = localStorage.getItem("eph-token");
    const token = JSON.parse(tokenObj).value || localStorage.getItem("AccessToken");

    // Generate finalText from question2
    const formattedQuestion2 = `<html>
    <head>
      <style>
        ul {
          list-style-type: disc;
          padding-left: 20px;
        }
      </style>
    </head>
    <body>
      <ul>
        ${formData.question2.map((item) => `<li>${item}</li>`).join("")}
      </ul>
    </body>
    </html>`;

    const rateCurrency = project?.cons_exp === "exp" ? formData.hourlyRateCurrency : formData.dailyRateCurrency;
    const rateValue = project?.cons_exp === "exp" ? formData.hourlyRate : formData.dailyRate;
    const rateFrequency = project?.cons_exp === "exp" ? "per hour" : "per day";

    const expertRate = `${rateCurrency} ${rateValue} ${rateFrequency}`;

    const applicationPayload = {
      project: project.id,
      expertID,
      question1,
      question2: formattedQuestion2,
      availability: groupConsecutiveTimestamps(formData.availability),
      uniqueId: generateUniqueId(),
      comment: formData.fullyAvailable ? formData.availabilityComment : "Fully available",
      recordConsent: formData.recordConsent,
      isSelfApplied: selfApplied,
      expertRate,
      education: formData.education,
      experience: formData.experience,
      position: formData.position,
      location: formData.location,
      language: formData.language,
      timezone: formData.timezone,
    };

    reactGa.event({
      category: "Consultant",
      action: "Apply to project",
      parameters: {
        consultant_id: `${expertID}`,
        project_id: project.id,
      },
    });

    try {
      // Submit main application
      const submitURL = selfApplied
        ? "consultant-request"
        : "submit-invitation";
      const appResponse = await fetch(`${API_URL}${submitURL}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(applicationPayload),
      });
      const appData = await appResponse.json();
      setHasSubmitted(false);

      if (appData.status === "success") {
        updateProjectStatus(project.id, "Waiting for review");
        onClose();
        Swal.fire({
          title: "Application submitted",
          icon: "success",
          showConfirmButton: false,
          timer: 1500
        });
      } else if (appData.status === "error") {
        onClose();
        Swal.fire({
          title: appData.message || "Submission failed",
          icon: "error",
          showConfirmButton: true,
          confirmButtonColor: "#365561"
        });
      }

      // Submit additional screening answers if available
      if (queAnswers && queAnswers.length > 0) {
        await fetch(`${API_URL}update-question-answers`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            projID: project.id,
            expertID: expertID,
            answers: queAnswers,
          }),
        });
      }
    } catch (error) {
      setHasSubmitted(false);
      onClose();
      Swal.fire({
        title: "Something Went Wrong!",
        icon: "error",
        confirmButtonColor: "#365561"
      });
    }
  };

  const commitAvailability = (nextValue) => {
    setAvailability((previousAvailability) => {
      const resolvedAvailability =
        typeof nextValue === "function"
          ? nextValue(previousAvailability)
          : nextValue;

      const normalizedAvailability = Array.from(
        new Set(
          (Array.isArray(resolvedAvailability)
            ? resolvedAvailability
            : []
          )
            .map(Number)
            .filter(Number.isFinite)
        )
      ).sort((a, b) => a - b);

      setFormData((previousFormData) => ({
        ...previousFormData,
        availability: normalizedAvailability,
        timezone,
      }));

      return normalizedAvailability;
    });
  };

  const handleRemoveSlot = (start, end) => {
    commitAvailability((previousAvailability) =>
      previousAvailability.filter(
        (slot) => slot < start || slot >= end
      )
    );
  };

  const handleSetAvailability = (slots) => {
    commitAvailability(slots);
  };

  const handleTimezoneChange = (nextTimezone) => {
    setTimezone(nextTimezone);
    setFormData((previousFormData) => ({
      ...previousFormData,
      timezone: nextTimezone,
    }));
  };


  const minVal = project?.cons_exp === "exp" ? 50 : 100;
  const maxVal = Number(
    project?.cons_exp === "exp" ? formData.hourlyRate : formData.dailyRate
  );
  const rateSteps = 5;
  const marks = Array.from({ length: rateSteps }, (_, i) => {
    const raw = minVal + (i / (rateSteps - 1)) * (maxVal - minVal);
    return Math.round(raw / 50) * 50;
  });

  const steps = [
    {
      title: "Summary of project fit",
      reason: (
        <div className="text-sm text-gray-500 italic">
          <p>
            We want our clients to understand the fit of a candidate with their specific requirements.
            That is why we want to understand how your background connects to this project.
            Please write one concise paragraph summarizing your relevant experience and your fit for the engagement.
            Please write the paragraph in English in the third person singular (he/she).
          </p>
          <p className="mt-2 font-semibold">Example:</p>
          <p className="text-sm text-gray-500 italic">
            "SAP consultant with over 30 years of total experience, more than 20 years of 
            which are in SAP project management, S/4HANA and SD/MM rollouts. He has strong..."
          </p>
        </div>
      ),
      content: (
        <div className="bg-white pt-4 pb-4">
          <label className="block text-m font-medium text-gray-700 mb-2">
            Your Professional Summary <span className="text-red-500">*</span>
          </label>
          <div
            ref={editableRef}
            contentEditable
            role="textbox"
            aria-multiline
            suppressContentEditableWarning
            placeholder='e.g., "I am a management consultant specializing in digital transformation and have supported several global clients in operational improvement projects."'
            className={`min-h-[120px] w-full p-3 rounded-xl text-sm bg-white outline-none transition-shadow duration-200 focus:shadow-[0_0_8px_rgba(54,85,97,0.4)]
              ${
                !canProceed
                  ? "border-orange-400 shadow-[0_0_0_2px_rgba(253,186,140,0.5)]"
                  : "border border-gray-300 focus:border-blue-400"
              }`}
            onInput={handleInput}
            onPaste={handlePaste}
            onBlur={(e) => {setQuestion1(e.target.innerText); setFormData({...formData, question1: e.target.innerText})}}
          >
            {formData.question1}
          </div>
          <div
            className={`mt-1 flex items-center ${
              hasSubmitted || overLimit ? "justify-between" : "justify-end"
            }`}
          >
            {hasSubmitted && !question1.trim() && (
              <p className="text-xs text-orange-500">This field is required.</p>
            )}
            {overLimit && (
              <p className="text-xs text-red-500">
                Text cannot exceed 500 characters.
              </p>
            )}
            <p className="text-right text-xs text-gray-500">{question1.length}/500</p>
          </div>
        </div>
      ),
    },
    {
      title: "Relevant project examples",
      reason: (
        <div className="text-sm text-gray-500 italic">
          <p>
            Add up to five recent projects that demonstrate your experience in the required area. 
            Use the 'Add another' button to add multiple relevant projects. 
            You can use up to 4000 characters per relevant project example.
          </p>
          <p className="mt-2 font-semibold">Example:</p>
          <p>
              "Conducting organizational cost benchmarks for engineering departments in a Tier 1 automotive supplier Leading the PMO for a multi-level supply chain optimization for an automotive OEM."
            </p>
        </div>
      ),
      content: (
        <div className="bg-white pt-4 pb-4">
          {question2.map((example, index) => (
            <div
              key={index}
              className={`${index === question2.length - 1 ? "mb-1" : "mb-3"}`}
            >
              <div className="flex flex-row justify-between items-center">
                <label className="flex flex-col sm:flex-row sm:gap-1 sm:items-center mb-1">
                  <p className="block text-md font-medium text-gray-500">
                    Relevant project {index + 1}
                  </p>
                  <p className="text-xs text-gray-500">(one paragraph, no bullets)</p>
                </label>

                {/* Delete button */}
                <button
                  type="button"
                  title="Remove example"
                  onClick={() => {
                    setQuestion2((prev) => {
                      const next = [...prev];
                      next.splice(index, 1);
                      const safe = next.length ? next : [""];
                      setFormData((fd) => ({ ...fd, question2: safe }));
                      return safe;
                    });
                  }}
                  className="text-gray-400 hover:text-red-500 text-sm focus:outline-none transition"
                >
                  <i className="fa-solid fa-trash"></i>
                </button>
              </div>

              {/* Textarea + per-field counter */}
              <div className="relative">
                <textarea
                  className="w-full min-h-[6rem] px-3 py-2 border border-gray-300 rounded-xl resize-y text-sm placeholder-gray-400 focus:outline-none focus:border-[#365561] focus:shadow-[0_0_8px_rgba(54,85,97,0.4)] focus:border-transparent"
                  placeholder="example: Conducting organizational cost benchmarks for engineering departments in a Tier 1 automotive supplier​ Leading the PMO for a multi-level supply chain optimization for an automotive OEM."
                  value={question2[index]}
                  maxLength={QUESTION2_LIMIT}
                  onChange={(e) => {
                    const value = e.target.value.slice(0, QUESTION2_LIMIT);

                    const newArr = [...question2];
                    newArr[index] = value;

                    setQuestion2(newArr);
                    setFormData((fd) => ({ ...fd, question2: newArr }));
                  }}
                />

                <p className="absolute bottom-2 right-3 text-xs text-gray-400">
                  {(question2[index]?.length || 0)}/{QUESTION2_LIMIT}
                </p>
              </div>
            </div>
          ))}

          {/* <div
            className={`flex items-center ${
              question2.length < 5 ? "justify-between" : "justify-end"
            }`}
          >
            {question2.length < 5 && (
              <button
                type="button"
                onClick={() => {
                  const next = [...question2, ""];
                  setQuestion2(next);
                  setFormData((fd) => ({ ...fd, question2: next }));
                }}
                className="text-[#365561] text-xs font-medium flex items-center gap-1 focus:outline-none"
              >
                <i className="fa-solid fa-plus" />
                Add another example
              </button>
            )}
          </div> */}

          <div className="flex items-center justify-center mt-3">
            {question2.length < 5 && (
              <button
                type="button"
                onClick={() => {
                  const next = [...question2, ""];
                  setQuestion2(next);
                  setFormData((fd) => ({ ...fd, question2: next }));
                }}
                className="
                  inline-flex items-center gap-2
                  px-4 py-2
                  rounded-full
                  border border-[#365561]/30
                  bg-white
                  text-gray-400
                  text-sm font-semibold
                  shadow-sm
                  transition-all duration-200
                  hover:text-[#365561] hover:shadow
                  focus:outline-none focus:ring-2 focus:ring-[#365561]/40
                "
              >
                <span
                  className="
                    inline-flex items-center justify-center
                    w-7 h-7
                    rounded-full
                    bg-[#365561]
                    text-white
                    text-base
                    shadow
                  "
                  aria-hidden="true"
                >
                  <i className="fa-solid fa-plus"></i>
                </span>
                Add another example
              </button>
            )}
          </div>

        </div>
      ),
    },
    {
      title:`Potential availability for ${project?.cons_exp === "exp" ? "Expert call" : "Get-to-know call"}`,
      reason: (
        <p className="text-sm text-gray-500 italic">
          We’d like to understand your potential availability for
          {project?.cons_exp === "exp" ? " the Expert call" : " a short ‘Get-to-know’ call"} if 
          the project moves forward. This helps our coordination team suggest suitable time slots 
          to the client and streamline scheduling. Please select a few time windows that would generally 
          work for you over the next few days — this isn’t a confirmed booking, just an indication of your availability.
        </p>
      ),
      content: (
        <div className="bg-white">
          <AvailabilityCalendar
            projID={project.id}
            expID={expertID}
            timezone={timezone}
            setTimezone={handleTimezoneChange}
            setAvailabilityForm={setAvailabilityForm}
            availability={availability}
            handleSetAvailability={handleSetAvailability}
            embeddedWizardMode={effectiveEmbeddedWizardMode}
          />
          {availability.length > 0 && (
            <section className="mt-3 rounded-2xl border border-slate-200 bg-slate-50/80 p-3 sm:p-4">
              <div className="mb-3 flex items-center justify-between gap-3">
                <div>
                  <h3 className="text-sm font-semibold text-slate-800">
                    Selected time slots
                  </h3>
                  <p className="mt-0.5 text-xs text-slate-500">
                    Tap the remove button to delete a time window.
                  </p>
                </div>
                <span className="rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-[#365561] shadow-sm">
                  {groupConsecutiveTimestamps(availability).length}
                </span>
              </div>

              <ul className="grid gap-2 sm:grid-cols-2">
                {groupConsecutiveTimestamps(availability).map(
                  ([start, end]) => (
                    <li
                      key={`${start}-${end}`}
                      className="flex min-w-0 items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm"
                    >
                      <div className="min-w-0">
                        <p className="truncate text-xs font-semibold text-slate-800">
                          {formatDate(start)}
                        </p>
                        <p className="mt-1 truncate text-[11px] text-slate-500">
                          Until {formatDate(end)} · {timezone}
                        </p>
                      </div>

                      <button
                        type="button"
                        onClick={() => handleRemoveSlot(start, end)}
                        className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-rose-50 text-rose-500 transition hover:bg-rose-100 hover:text-rose-700 focus:outline-none focus:ring-2 focus:ring-rose-200"
                        aria-label="Remove selected time slot"
                      >
                        <i className="fa-solid fa-xmark" />
                      </button>
                    </li>
                  )
                )}
              </ul>
            </section>
          )}
        </div>
      ),
    },
    {
      title: "Availability for the project",
      reason: (
        <p className="text-sm text-gray-500 italic">
          We‘d like to understand if you are fully available for the project during the expected period/duration 
          or if you have any limitations or obstacles. This helps our team to communicate transparently to the 
          client in case there are any restrictions. Please indicate if you are fully available, and if not, 
          briefly explain any limitations or constraints.
        </p>
      ),
      content: (
        <div className="bg-white pt-4 pb-4">
          <div className="flex flex-col sm:flex-row justify-between">
            <label className="text-m font-medium text-gray-700">
              Are you fully available for this project?
            </label>
            <div className="flex items-center gap-3">
              <label
                htmlFor="fullyAvailable"
                className="text-sm text-gray-600"
              >
                {fullyAvailable ? "Yes" : "No"}
              </label>

              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  id="fullyAvailable"
                  checked={fullyAvailable === true}
                  onChange={() =>
                    {setFullyAvailable(!fullyAvailable); setFormData({...formData, fullyAvailable: !fullyAvailable})}
                  }
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-300 rounded-full peer peer-checked:bg-[#365561] transition-colors"></div>
                <div className="absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full shadow-md transform peer-checked:translate-x-5 transition-transform" />
              </label>
            </div>
          </div>

          {fullyAvailable === false && (
            <textarea
              className="w-full mt-2 min-h-[5rem] p-2 border border-gray-300 rounded-xl resize-y text-sm sm:text-sm placeholder-gray-400 focus:outline-none focus:shadow-[0_0_8px_rgba(54,85,97,0.4)] focus:border-transparent"
              value={availabilityComment}
              onChange={(e) =>
                {setAvailabilityComment(e.target.value); setFormData({...formData, availabilityComment: e.target.value})}
              }
              placeholder="Please provide additional comments"
            />
          )}
        </div>
      ),
    },
    {
      title: "Rate expectation",
      reason: (
        <p className="text-sm text-gray-500 italic">
          We ask for your rate expectation to ensure this project aligns with your compensation requirements before moving forward. 
          This helps our team quickly assess whether there is a realistic match and prevents misalignment later in the process. 
          Please indicate your rate expectation for this specific project opportunity. Keep in mind that the rate is an 
          important parameter in the selection process for our clients.
        </p>
      ),
      content: (
        <div className="bg-white pt-4 pb-4">
          <div className="flex flex-col gap-6 md:flex-row items-start md:items-center md:justify-between">
            
            <label className="text-m font-medium text-gray-700">
              What is your {project?.cons_exp === "exp" ? "hourly" : "daily"} rate expectation? <span className="text-red-500">*</span>
            </label>

            <div className="flex flex-row items-start sm:items-center gap-3">
              {/* Currency Select */}
              <div className="relative w-[90px]">
                <select
                  value={
                    project?.cons_exp === "exp"
                      ? formData.hourlyRateCurrency
                      : formData.dailyRateCurrency
                  }
                  onChange={(e) => {
                    if (project?.cons_exp === "exp") {
                      setHourlyRateCurrency(e.target.value);
                      setFormData({ ...formData, hourlyRateCurrency: e.target.value });
                    } else {
                      setDailyRateCurrency(e.target.value);
                      setFormData({ ...formData, dailyRateCurrency: e.target.value });
                    }
                  }}
                  className={`appearance-none w-full bg-white border-b-[1px] ${!(project?.cons_exp === "exp" ? formData.hourlyRateCurrency : formData.dailyRateCurrency) ? 'border-red-500' : 'border-gray-300'} text-center text-sm rounded-md py-0.5 pr-8 hover:border-b-[#365561] focus:outline-none focus:ring-[#365561] cursor-pointer`}
                >
                  {/* <option value="" disabled className="text-gray-300">
                    currency
                  </option> */}
                  {currencies.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>

                <i className="fa-solid fa-chevron-down pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-gray-400"></i>
              </div>
              
              <div className="flex items-center gap-3">
                {/* Rate Input */}
                <input
                  type="number"
                  className={`w-1/2 border-b ${!(project?.cons_exp === "exp" ? formData.hourlyRate : formData.dailyRate) ? 'border-red-500' : 'border-gray-300'} text-center hover:border-[#365561] focus:border-[#365561] focus:outline-none text-sm pb-1`}
                  placeholder="only numbers"
                  value={project?.cons_exp === "exp" ? hourlyRate : dailyRate}
                  onChange={(e) => {
                    if (project?.cons_exp === "exp") {
                      setHourlyRate(e.target.value);
                      setFormData({ ...formData, hourlyRate: e.target.value });
                      const threshold = getHourlyThreshold(formData.hourlyRateCurrency);
                      const numericRate = Number(e.target.value);
                      if ( numericRate > threshold) {
                        setShowNegotiation(true);
                        setNegotiatedRate(numericRate); // default slider starting point
                      } else {
                        setShowNegotiation(false);
                      }

                    } else {
                      setDailyRate(e.target.value);
                      setFormData({ ...formData, dailyRate: e.target.value });
                      const threshold = getDailyThreshold(formData.dailyRateCurrency);
                      const numericRate = Number(e.target.value);
                      if (numericRate > threshold) {
                        setShowNegotiation(true);
                        setNegotiatedRate(numericRate);
                      } else {
                        setShowNegotiation(false);
                      }

                    }
                  }}
                />

                <span className="text-sm text-gray-500 min-w-[100px]">
                  {project?.cons_exp === "exp" ? "per hour" : "per day"}
                </span>
              </div>
            </div>
          </div>

          {showNegotiation && (
            <div className="mt-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">

              <p className="text-sm text-gray-700 mb-4">
                You are free to set your rate as you see fit. Be advised, however, that for this
                project your rate exceeds the rate of other Consultants who have been invited. 
                For projects like this one - where similarly qualified Consultants may be available - 
                your rate could be a factor in determining whether you are selected. If you are inclined 
                to do so, you can modify your rate (for this project only).
              </p>


              <div className="relative w-full mt-10 mb-10">

                {/* Bubble */}
                <div
                  className="absolute -top-10 px-3 py-1 rounded-full bg-[#365561] shadow text-sm font-medium text-white"
                  style={{
                    left: `${((negotiatedRate - minVal) / (maxVal - minVal)) * 100}%`,
                    transform: "translateX(-50%)",
                  }}
                >
                  {negotiatedRate}
                  <div className="absolute left-1/2 -bottom-2 transform -translate-x-1/2 w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-[#365561]"></div>
                </div>

                {/* Slider */}
                <input
                  type="range"
                  min={minVal}
                  max={maxVal}
                  step="5"
                  value={negotiatedRate}
                  onChange={(e) => setNegotiatedRate(Number(e.target.value))}
                  className="w-full custom-slider accent-[#365561]"
                  list="rate-marks"
                />

                {/* Marks (5 steps only) */}
                <datalist id="rate-marks">
                  {marks.map((mark) => (
                    <option key={mark} value={mark} label={mark}/>
                  ))}
                </datalist>

                {/* Visible Labels */}
                <div className="relative w-full">
                  {marks.map((mark) => {
                    const percent = ((mark - minVal) / (maxVal - minVal)) * 100;

                    return (
                      <div
                        key={mark}
                        className="absolute text-xs text-gray-600"
                        style={{
                          left: `${percent}%`,
                          transform: "translateX(-50%)",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {mark}
                      </div>
                    );
                  })}
                </div>
              </div>


              <p className="mt-4 text-sm font-semibold">
                Adjusted Rate:{" "}
                <span className="text-[#365561]">{negotiatedRate}</span>{" "}
                {project?.cons_exp === "exp" ? "per hour" : "per day"}
              </p>

              {/* Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 mt-4">
                <button
                  onClick={() => {
                    if (project?.cons_exp === "exp") {
                      setHourlyRate(negotiatedRate);
                      setFormData({ ...formData, hourlyRate: negotiatedRate });
                    } else {
                      setDailyRate(negotiatedRate);
                      setFormData({ ...formData, dailyRate: negotiatedRate });
                    }
                    setShowNegotiation(false);
                  }}
                  className="px-4 py-2 bg-[#365561] text-white rounded-full text-sm hover:bg-[#2c444f]"
                >
                  Apply adjusted rate
                </button>

                <button
                  onClick={() => setShowNegotiation(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-full text-sm hover:bg-gray-400"
                >
                  Keep my original rate
                </button>
              </div>
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Consent to recording",
      reason: (
        <p className="text-sm text-gray-700 mb-4">
          Occasionally, client calls might be recorded. 
          Your consent allows us to proceed with recording should the client requests it. 
          Declining might limit the ability to participate in a call, depending on the specific requirements.
       </p>
      ),
      content: (
        <div className="bg-white pt-4 pb-4">
          <div className="flex flex-col sm:flex-row justify-between sm:items-center">
            <label className="text-m font-medium text-gray-700">
              Do you consent to being recorded during the consultation?
            </label>
            <div className="flex items-center gap-3">
              <label
                htmlFor="presentSwitch"
                className="text-sm text-gray-600"
              >
                {recordConsent ? "Yes" : "No"}
              </label>

              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  id="presentSwitch"
                  checked={recordConsent === true}
                  onChange={() => {setRecordConsent(!recordConsent); setFormData({...formData, recordConsent: !recordConsent})}}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-300 rounded-full peer peer-checked:bg-[#365561] transition-colors"></div>
                <div className="absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full shadow-md transform peer-checked:translate-x-5 transition-transform" />
              </label>
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "Review application",
      reason: (
        <p className="text-sm text-gray-700 mb-6">
          Review and modify it if necessary before you submit your application.
       </p>
      ),
      content: (
        <div className="bg-gray-100 rounded-[10px]">
          <ReviewProfile expertID={expertID} project={project} formData={formData} setFormData={setFormData} />
        </div>
      ),
    },
  ];

  // Keep local rate states in sync when formData is changed elsewhere (e.g., ReviewProfile)
  useEffect(() => {
    setHourlyRate(formData.hourlyRate ?? "");
  }, [formData.hourlyRate]);

  useEffect(() => {
    setDailyRate(formData.dailyRate ?? "");
  }, [formData.dailyRate]);

  // Optional but recommended: also sync currencies because you use dailyRateCurrency state in threshold logic
  useEffect(() => {
    setHourlyRateCurrency(formData.hourlyRateCurrency ?? "EUR");
  }, [formData.hourlyRateCurrency]);

  useEffect(() => {
    setDailyRateCurrency(formData.dailyRateCurrency ?? "EUR");
  }, [formData.dailyRateCurrency]);

  // Keep local question2 in sync when formData is changed elsewhere (e.g., ReviewProfile)
  useEffect(() => {
    if (Array.isArray(formData.question2)) {
      setQuestion2(formData.question2.length ? formData.question2 : [""]);
    }
  }, [formData.question2]);

  const handleTimelineNavigation = async (targetStep) => {
    const isCompletedStage =
      targetStep >= 0 &&
      targetStep <= highestCompletedStage;

    if (
      !isCompletedStage ||
      targetStep === step ||
      stageSaving ||
      draftLoading
    ) {
      return;
    }

    const saved = await saveStage({
      activeStage: targetStep,
      completedStage: highestCompletedStage,
    });

    if (saved) {
      setHasSubmitted(false);
      setStep(targetStep);
    }
  };

  if (!project) return null;

  const stepLabels = [
    "Project fit",
    "Project example",
    "Call availability",
    "Project availability",
    "Rate",
    "Consent",
    "Review",
  ];

  const mobileTimelineItems = getMobileTimelineItems(
    step,
    stepLabels
  );

  return (
    <>
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-1.5 backdrop-blur-sm sm:px-4 sm:py-3"
        >
          <motion.div
            initial={{ y: 18, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 18, opacity: 0, scale: 0.98 }}
            transition={{ type: "spring", stiffness: 320, damping: 28 }}
            className="relative top-[40px] flex h-[calc(95dvh-40px)] w-full min-w-0 max-w-[calc(100vw-12px)] flex-col overflow-hidden rounded-2xl bg-[radial-gradient(circle_at_top,rgba(54,85,97,0.18),transparent_35%),linear-gradient(to_bottom,#f8fafc,#eef2f6)] shadow-[0_24px_80px_rgba(15,23,42,0.32)] sm:h-[calc(95dvh-40px)] sm:max-w-7xl sm:rounded-3xl"
          >
            <header className="w-full min-w-0 shrink-0 overflow-hidden border-b border-slate-200/80 bg-white/90 px-3 pb-2.5 pt-3 backdrop-blur sm:px-6 sm:pb-3 sm:pt-4">
              <div className="relative w-full min-w-0 pr-12 sm:pr-14">
                <div className="w-full min-w-0">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#365561] sm:text-xs sm:tracking-[0.17em]">
                    {selfApplied ? "Project application" : "Project invitation"}
                  </p>
                  <h2 className="mt-0.5 max-w-full break-words text-base font-semibold leading-5 text-slate-900 [overflow-wrap:anywhere] sm:mt-1 sm:truncate sm:text-xl sm:leading-7">
                    {project?.projectName || project?.name || `Project ${project?.id}`}
                  </h2>
                  <div className="mt-1 flex min-w-0 flex-wrap items-center gap-2 text-[11px] text-slate-500">
                    {stageSaving && <span>Saving progress…</span>}
                    {!stageSaving && lastSavedAt && <span>Progress saved</span>}
                    {draftRestored && (
                      <span className="rounded-full bg-emerald-50 px-2 py-0.5 font-semibold text-emerald-700">
                        Resumed
                      </span>
                    )}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={onClose}
                  className="absolute right-0 top-0 inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:bg-slate-50 hover:text-red-500 focus:outline-none"
                  aria-label="Close application"
                >
                  <i className="fa-solid fa-xmark" />
                </button>
              </div>

              <div className="mt-3 w-full min-w-0 overflow-hidden sm:mt-4">
                {/* Compact embedded timeline for phones */}
                <div className="w-full min-w-0 overflow-hidden px-0.5 sm:hidden">
                  <div
                    className="grid w-full min-w-0 items-start"
                    style={{
                      gridTemplateColumns: `repeat(${mobileTimelineItems.length}, minmax(0, 1fr))`,
                    }}
                  >
                    {mobileTimelineItems.map((item, itemIndex) => {
                      const isStage = item.kind === "stage";
                      const itemStage = isStage ? item.index : null;
                      const isActive = isStage && itemStage === step;
                      const isCompleted =
                        isStage &&
                        itemStage <= highestCompletedStage &&
                        !isActive;
                      const isPastCounter =
                        item.kind === "counter" && item.side === "past";
                      const targetStep = isStage
                        ? itemStage
                        : item.targetIndex;
                      const isNavigable =
                        targetStep !== null &&
                        targetStep !== step &&
                        targetStep <= highestCompletedStage;
                      const connectorComplete = isStage
                        ? itemStage <= highestCompletedStage
                        : isPastCounter;
                      const inRight = itemIndex > step;

                      return (
                        <div
                          key={item.key}
                          className="relative min-w-0"
                        >
                          {itemIndex < mobileTimelineItems.length - 1 && (
                            <span
                              className={[
                                "pointer-events-none absolute left-[calc(50%+14px)] top-[13px] h-0.5 w-[calc(100%-28px)]",
                                connectorComplete
                                  ? "bg-[#365561]"
                                  : "bg-slate-200",
                              ].join(" ")}
                            />
                          )}

                          <button
                            type="button"
                            disabled={
                              !isNavigable || stageSaving || draftLoading
                            }
                            onClick={() => {
                              if (isNavigable) {
                                handleTimelineNavigation(targetStep);
                              }
                            }}
                            className={[
                              "relative z-10 flex w-full min-w-0 flex-col items-center text-center focus:outline-none",
                              isNavigable
                                ? "cursor-pointer"
                                : "cursor-default",
                              stageSaving || draftLoading
                                ? "opacity-60"
                                : "",
                            ].join(" ")}
                          >
                            <span className="flex h-7 w-full items-center justify-center">
                              {item.kind === "counter" ? (
                                <span
                                  className={[
                                    "inline-flex h-7 min-w-7 items-center justify-center rounded-full px-1.5 text-[10px] font-bold",
                                    isPastCounter
                                      ? "bg-slate-200 text-slate-600"
                                      : "border border-slate-200 bg-white text-slate-400",
                                  ].join(" ")}
                                >
                                  {item.count}+
                                </span>
                              ) : (
                                <span
                                  className={[
                                    "inline-flex items-center justify-center rounded-full transition",
                                    isActive
                                      ? "h-4 w-4 bg-[#365561] shadow-[0_0_0_5px_rgba(54,85,97,0.14)]"
                                      : isCompleted
                                        ? inRight
                                          ? "h-5 w-5 bg-slate-300 text-white"
                                          : "h-5 w-5 bg-[#365561] text-white"
                                        : "h-5 w-5 border-2 border-slate-300 bg-white",
                                  ].join(" ")}
                                >
                                  {isCompleted && (
                                    <i className="fa-solid fa-check text-[9px] text-white" />
                                  )}
                                </span>
                              )}
                            </span>

                            <span
                              className={[
                                "mt-1.5 block w-full min-w-0 px-1 text-[9px] font-semibold leading-3 [overflow-wrap:anywhere]",
                                isActive
                                  ? "text-[#365561]"
                                  : isCompleted || isPastCounter
                                    ? "text-slate-600"
                                    : "text-slate-400",
                              ].join(" ")}
                            >
                              {item.kind === "counter"
                                ? item.side === "past"
                                  ? "Earlier"
                                  : "More"
                                : item.label}
                            </span>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Full timeline for tablets and desktop */}
                <div className="hidden overflow-x-auto px-1 pb-3 pt-2 sm:block">
                  <div className="flex min-w-max items-start px-1 sm:min-w-0">
                    {stepLabels.map((label, index) => {
                      const isActive = index === step;
                      const isCompleted =
                        index <= highestCompletedStage && !isActive;
                      const isNavigable =
                        index <= highestCompletedStage && !isActive;
                      const inRight = index > step;

                      return (
                        <React.Fragment key={label}>
                          <button
                            type="button"
                            disabled={
                              !isNavigable || stageSaving || draftLoading
                            }
                            onClick={() => {
                              if (isNavigable) {
                                handleTimelineNavigation(index);
                              }
                            }}
                            className={[
                              "group flex w-[104px] shrink-0 flex-col items-center text-center focus:outline-none sm:min-w-0 sm:flex-1",
                              isNavigable
                                ? "cursor-pointer"
                                : "cursor-default",
                              stageSaving || draftLoading
                                ? "opacity-60"
                                : "",
                            ].join(" ")}
                          >
                            <span
                              className={[
                                "relative z-10 inline-flex items-center justify-center rounded-full transition",
                                isActive
                                  ? "h-4 w-4 bg-[#365561] shadow-[0_0_0_5px_rgba(54,85,97,0.14)]"
                                  : isCompleted
                                    ? inRight
                                      ? "h-5 w-5 bg-slate-300 text-white"
                                      : "h-5 w-5 bg-[#365561] text-white"
                                    : "h-5 w-5 border-2 border-slate-300 bg-white",
                              ].join(" ")}
                            >
                              {isCompleted && (
                                <i className="fa-solid fa-check text-[9px] text-white" />
                              )}
                            </span>

                            <span
                              className={[
                                "mt-2 max-w-[100px] text-[10px] font-semibold leading-4 transition sm:text-[11px]",
                                isActive
                                  ? "text-[#365561]"
                                  : index <= highestCompletedStage
                                    ? "text-slate-600"
                                    : "text-slate-400",
                              ].join(" ")}
                            >
                              {label}
                            </span>
                          </button>

                          {index < stepLabels.length - 1 && (
                            <span
                              className={[
                                "mt-[9px] h-0.5 min-w-[26px] flex-1",
                                index <= highestCompletedStage
                                  ? "bg-[#365561]"
                                  : "bg-slate-200",
                              ].join(" ")}
                            />
                          )}
                        </React.Fragment>
                      );
                    })}
                  </div>
                </div>
              </div>
            </header>

            <div className="min-h-0 flex-1 overflow-hidden p-2 sm:p-4">
              <main className="h-full min-h-0 overflow-y-auto rounded-2xl border border-slate-200/80 bg-white shadow-[0_10px_30px_rgba(15,23,42,0.06)] sm:rounded-3xl">
                {draftLoading ? (
                  <div className="flex h-full min-h-[420px] items-center justify-center">
                    <div className="text-center text-[#365561]">
                      <i className="fa-solid fa-spinner fa-spin text-2xl" />
                      <p className="mt-3 text-sm font-semibold">Loading your application…</p>
                    </div>
                  </div>
                ) : (
                  <motion.div
                    key={step}
                    initial={{ opacity: 0, x: 12 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -12 }}
                    className="mx-auto w-full max-w-5xl px-3 py-3 sm:px-6 sm:py-6"
                  >
                    <div className="rounded-2xl bg-[#365561]/5 p-3 sm:p-4">
                      <h3 className="text-base font-semibold text-slate-900 sm:text-xl">{steps[step].title}</h3>
                      <div className="mt-1.5 text-xs leading-5 text-slate-500 sm:mt-2 sm:text-sm sm:leading-6">{steps[step].reason}</div>
                    </div>
                    <div className="mt-3 sm:mt-4">{steps[step].content}</div>
                  </motion.div>
                )}
              </main>
            </div>

            <footer className="shrink-0 border-t border-slate-200 bg-white px-3 py-2.5 sm:px-6 sm:py-3">
              <div className={step === 0 ? "flex justify-end" : "flex items-center justify-between gap-3"}>
                {step > 0 && (
                  <button
                    type="button"
                    onClick={prevStep}
                    disabled={stageSaving || draftLoading}
                    className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 shadow-sm transition focus:outline-none hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </button>
                )}

                {step < totalSteps - 1 ? (
                  <button
                    type="button"
                    onClick={nextStep}
                    disabled={stageSaving || draftLoading}
                    className="inline-flex items-center gap-2 rounded-full bg-[#365561] px-5 py-2 text-sm font-semibold text-white shadow-[0_10px_24px_rgba(54,85,97,0.22)] transition hover:bg-[#2c4650] focus:outline-none disabled:cursor-not-allowed disabled:opacity-45"
                  >
                    {stageSaving ? <i className="fa-solid fa-spinner fa-spin" /> : null}
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={submitApplication}
                    disabled={stageSaving || draftLoading}
                    className="inline-flex items-center gap-2 rounded-full bg-[#365561] px-5 py-2 text-sm font-semibold text-white shadow-[0_10px_24px_rgba(54,85,97,0.22)] transition hover:bg-[#2c4650] focus:outliine-none disabled:cursor-not-allowed disabled:opacity-45"
                  >
                    {stageSaving ? (
                      <i className="fa-solid fa-spinner fa-spin" />
                    ) : (
                      <i className="fa-solid fa-paper-plane" />
                    )}
                    {selfApplied ? "Submit application" : "Submit invitation response"}
                  </button>
                )}
              </div>
            </footer>
          </motion.div>
        </motion.div>
      </AnimatePresence>

      {availabilityForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 px-2">
          <div className="w-full mb-6">
            <div className="bg-white shadow-lg w-full h-[calc(100vh-96px)] mt-[24px] overflow-hidden relative rounded-xl">
              <AvailabilityCalendar
                projID={project.id}
                expID={expertID}
                timezone={timezone}
                setTimezone={handleTimezoneChange}
                setAvailabilityForm={setAvailabilityForm}
                availability={availability}
                handleSetAvailability={handleSetAvailability}
                embeddedWizardMode={effectiveEmbeddedWizardMode}
              />
            </div>
          </div>
        </div>
      )}

      <style>
        {`
          @layer components {
            .custom-slider::-webkit-slider-thumb {
              -webkit-appearance: none;
              height: 26px;
              width: 26px;
              background: #365561;
              border-radius: 50%;
              border: 3px solid white;
              box-shadow: 0 0 0 2px #365561;
              cursor: grab;
            }

            .custom-slider::-webkit-slider-runnable-track {
              height: 12px;
              border-radius: 9999px;
              background-color: #9aa5a5;
            }

            .custom-slider:focus::-webkit-slider-thumb {
              outline: none;

            }
          }

        `}
      </style>
    </>
  );
}

const LANGUAGE_LIST = [
  "Afrikaans", "Albanian", "Arabic", "Armenian", "Basque", "Bengali", "Bulgarian", "Catalan", "Cambodian", "Chinese (Mandarin)", "Croatian", "Czech", "Danish", "Dutch", "English", "Estonian",
  "Fiji", "Finnish", "French", "Georgian", "German", "Greek", "Gujarati", "Hebrew", "Hindi", "Hungarian", "Icelandic", "Indonesian", "Irish", "Italian", "Japanese", "Javanese", "Korean",
  "Latin", "Latvian", "Lithuanian", "Macedonian", "Malay", "Malayalam", "Maltese", "Maori", "Marathi", "Mongolian", "Nepali", "Norwegian", "Persian", "Polish", "Portuguese", "Punjabi",
  "Quechua", "Romanian", "Russian", "Samoan", "Serbian", "Slovak", "Slovenian", "Spanish", "Swahili", "Swedish", "Tamil", "Tatar", "Telugu", "Thai", "Tibetan", "Tonga", "Turkish",
  "Ukrainian", "Urdu", "Uzbek", "Vietnamese", "Welsh", "Xhosa",
];
const languageLevel = ['Native', 'Advanced', 'Proficiency', 'Basic'];
const REGION_LIST = ["Europe", "Middle East", "Asia Pacific", "Africa", "Latin America", "North America", "Global"];
const COUNTRY_LIST = [
  "Afghanistan", "Åland Islands", "Albania", "Algeria", "American Samoa", "Andorra", "Angola", "Anguilla", "Antarctica", "Antigua and Barbuda", 
  "Argentina", "Armenia", "Aruba", "Australia", "Austria", "Azerbaijan", "Bahamas", "Bahrain", "Bangladesh", "Barbados", "Belarus", "Belgium", "Belize", "Benin", "Bermuda", 
  "Bhutan", "Bolivia", "Bosnia and Herzegovina", "Botswana", "Bouvet Island", "Brazil", "British Indian Ocean Territory", "Brunei Darussalam", "Bulgaria", "Burkina Faso", 
  "Burundi", "Cambodia", "Cameroon", "Canada", "Cape Verde", "Cayman Islands", "Central African Republic", "Chad", "Chile", "China", "Christmas Island", "Cocos (Keeling) Islands", 
  "Colombia", "Comoros", "Congo", "Congo, The Democratic Republic of The", "Cook Islands", "Costa Rica", "Cote D'ivoire", "Croatia", "Cuba", "Cyprus", "Czech Republic", "Denmark", 
  "Djibouti", "Dominica", "Dominican Republic", "Ecuador", "Egypt", "El Salvador", "Equatorial Guinea", "Eritrea", "Estonia", "Ethiopia", "Falkland Islands (Malvinas)", "Faroe Islands", 
  "Fiji", "Finland", "France", "French Guiana", "French Polynesia", "French Southern Territories", "Gabon", "Gambia", "Georgia", "Germany", "Ghana", "Gibraltar", "Greece", "Greenland", 
  "Grenada", "Guadeloupe", "Guam", "Guatemala", "Guernsey", "Guinea", "Guinea-bissau", "Guyana", "Haiti", "Heard Island and Mcdonald Islands", "Holy See (Vatican City State)", "Honduras", 
  "Hong Kong", "Hungary", "Iceland", "India", "Indonesia", "Iran, Islamic Republic of", "Iraq", "Ireland", "Isle of Man", "Israel", "Italy", "Jamaica", "Japan", "Jersey", "Jordan", 
  "Kazakhstan", "Kenya", "Kiribati", "Korea, Democratic People's Republic of", "Korea, Republic of", "Kuwait", "Kyrgyzstan", "Lao People's Democratic Republic", "Latvia", "Lebanon", 
  "Lesotho", "Liberia", "Libyan Arab Jamahiriya", "Liechtenstein", "Lithuania", "Luxembourg", "Macao", "Macedonia, The Former Yugoslav Republic of", "Madagascar", "Malawi", "Malaysia", 
  "Maldives", "Mali", "Malta", "Marshall Islands", "Martinique", "Mauritania", "Mauritius", "Mayotte", "Mexico", "Micronesia, Federated States of", "Moldova, Republic of", "Monaco", 
  "Mongolia", "Montenegro", "Montserrat", "Morocco", "Mozambique", "Myanmar", "Namibia", "Nauru", "Nepal", "Netherlands", "Netherlands Antilles", "New Caledonia", "New Zealand", "Nicaragua", 
  "Niger", "Nigeria", "Niue", "Norfolk Island", "Northern Mariana Islands", "Norway", "Oman", "Pakistan", "Palau", "Palestinian Territory, Occupied", "Panama", "Papua New Guinea", 
  "Paraguay", "Peru", "Philippines", "Pitcairn", "Poland", "Portugal", "Puerto Rico", "Qatar", "Reunion", "Romania", "Russian Federation", "Rwanda", "Saint Helena", "Saint Kitts and Nevis", 
  "Saint Lucia", "Saint Pierre and Miquelon", "Saint Vincent and The Grenadines", "Samoa", "San Marino", "Sao Tome and Principe", "Saudi Arabia", "Senegal", "Serbia", "Seychelles", 
  "Sierra Leone", "Singapore", "Slovakia", "Slovenia", "Solomon Islands", "Somalia", "South Africa", "South Georgia and The South Sandwich Islands", "Spain", "Sri Lanka", "Sudan", 
  "Suriname", "Svalbard and Jan Mayen", "Swaziland", "Sweden", "Switzerland", "Syrian Arab Republic", "Taiwan, Province of China", "Tajikistan", "Tanzania, United Republic of", "Thailand", 
  "Timor-leste", "Togo", "Tokelau", "Tonga", "Trinidad and Tobago", "Tunisia", "Turkey", "Turkmenistan", "Turks and Caicos Islands", "Tuvalu", "Uganda", "Ukraine", "United Arab Emirates", 
  "United Kingdom", "United States", "United States Minor Outlying Islands", "Uruguay", "Uzbekistan", "Vanuatu", "Venezuela", "Viet Nam", "Virgin Islands, British", "Virgin Islands, U.S.", 
  "Wallis and Futuna", "Western Sahara", "Yemen", "Zambia", "Zimbabwe"
];

function ReviewProfile({ expertID, project, formData, setFormData }) {
  const { API_URL } = useContext(UrlContext);
  const mediaLink = "https://media.licdn.com/";
  const databaseLink = "https://images.expertpowerhouse.com";
  const expertName = 'Demo Profile';
  const currencies = ["EUR", "USD", "GBP", "CHF", "AUD", "AED", "INR"];
  const [profileExperience, setProfileExperience] = useState([]);
  const [profileName, setProfileName] = useState('Demo Profile');
  const [profilePicturePreview, setProfilePicturePreview] = useState('');
  const [location, setLocation] = useState('');
  const [languages, setLanguages] = useState([]);
  const [profileEducation, setProfileEducation] = useState([]);

  const rateCurrency = project?.cons_exp === "exp" ? formData.hourlyRateCurrency : formData.dailyRateCurrency;
  const rateValue = project?.cons_exp === "exp" ? formData.hourlyRate : formData.dailyRate;
  const rateFrequency = project?.cons_exp === "exp" ? "per hour" : "per day";
  const expertRate = `${rateCurrency} ${rateValue} ${rateFrequency}`;
  const [isSummaryEdit, setIsSummaryEdit] = useState(false);
  const [isEducationEdit, setIsEducationEdit] = useState(false);
  const [isInfoEdit, setIsInfoEdit] = useState(false);
  const [isRateEdit, setIsRateEdit] = useState(false);
  const [isExperienceEdit, setIsExperienceEdit] = useState(false);

  const [isDragging, setIsDragging] = useState(false);
  const clickTimeout = useRef(null);
  const dragStartRef = useRef(false);
  const navigate = useNavigate();

  const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const YEARS = Array.from({ length: 60 }, (_, i) => String(new Date().getFullYear() - i));
  const [isLogoModalOpen, setIsLogoModalOpen] = useState(false);
  const [activeLogoExpIndex, setActiveLogoExpIndex] = useState(null);
  const [tempCompanyLogo, setTempCompanyLogo] = useState("");
  const logoDropArea = useRef(null);
  const CURRENT_VALUES = ["present", "heute"];

  const [city, setCity] = useState("");
  const [region, setRegion] = useState("");
  const [country, setCountry] = useState("");
  const [languageList, setLanguageList] = useState(languages || []);
  const [newLanguage, setNewLanguage] = useState({ languageName: "", level: "" });
  const [levelMissing, setLevelMissing] = useState(false);
  const [flashAddLanguage, setFlashAddLanguage] = useState(false);
  const [isSavingInfo, setIsSavingInfo] = useState(false);

  const profileSummary = `
    ${formData.question1}

    <h3>Relevant project experience</h3>
    <ul>
      ${formData.question2.map(exp => `<li>${exp}</li>`).join("")}
    </ul>
  `;

  const getExpertExperience = async () => {
    const tokenObj = localStorage.getItem("eph-token");
    const token = JSON.parse(tokenObj).value || localStorage.getItem("AccessToken");
    const requestBody = {
      expertID: expertID,
    };

    try {
      let modifiedUrl = "fetch-expert-experience";
      const response = await fetch(`${API_URL}${modifiedUrl}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      const responseData = await response.json();

      if (responseData.status === "success") {
        const normalizedExperience = enrichExperienceDates(
          responseData.expertExperience || []
        );

        setProfileExperience(normalizedExperience);

        setFormData((prev) => ({
          ...prev,
          experience: normalizedExperience,
        }));
      } else if (responseData.status === "error") {
        console.error("Error fetching expert experience:", responseData.text);
      } else {
        console.error("Error fetching expert experience:", responseData.text);
      }
    } catch (error) {
      console.error("Network or unexpected error:", error);
    }
  };

  // fetch expert data
  const fetchExpert = async () => {
    const tokenObj = localStorage.getItem("eph-token");
    const token = JSON.parse(tokenObj).value || localStorage.getItem("AccessToken");
    const url = `${API_URL}expert-details/${expertID}`;

    try {
      const res = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const data = await res.json();

      // Handle unauthorized, token expired, etc.
      const isAuthError =
        res.status === 401 ||
        res.status === 403 ||
        /unauthorized|token|expired/i.test(data.text || "");

      if (isAuthError) {
        localStorage.removeItem("AccessToken");
        navigate("/login");
        return;
      }

      // Success
      setProfileName(data.expert.name);
      setLocation([data.expert.city, data.expert.country].filter(Boolean).join(", ") || data.expert.region || "No information");
      setCity(data.expert.city);
      setCountry(data.expert.country);
      setRegion(data.expert.region);
      setFormData((prev) => ({
        ...prev,
        location: [data.expert.city, data.expert.country].filter(Boolean).join(", ") || data.expert.region || "No information",
      }));

      const profilePic = data.expert.profilePicture;
      if (!profilePic) {
        setProfilePicturePreview(null);
      } else {
        const isFullURL = profilePic.includes(mediaLink);
        setProfilePicturePreview(
          isFullURL ? profilePic : `${databaseLink}${profilePic}`
        );
      }

      if (data.expert?.education) {
        setFormData((prev) => ({
          ...prev,
          education: data.expert.education,
        }));
        const months = [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ];
        const educationArray = data.expert?.education
          .split("\n\n")
          .filter((e) => e.trim() !== "")
          .sort((a, b) => {
            const aLines = a.split("\n");
            const bLines = b.split("\n");

            const aDate = aLines[2]?.split(" - ")[1];
            const bDate = bLines[2]?.split(" - ")[1];

            if (aDate === "Present" && bDate !== "Present") return -1;
            if (bDate === "Present" && aDate !== "Present") return 1;

            if (aDate && bDate) {
              const [aMonth, aYear] = aDate.split(" ");
              const [bMonth, bYear] = bDate.split(" ");
              const monthIndex = (m) => months.indexOf(m);
              const aVal = new Date(Number(aYear), monthIndex(aMonth));
              const bVal = new Date(Number(bYear), monthIndex(bMonth));
              return bVal - aVal;
            }
            return 0;
          });
        setProfileEducation(educationArray);
      }
    } catch (err) {
      console.error("Network or unexpected error:", err);
    }
  };

  const getLanguages = async () => {
    const tokenObj = localStorage.getItem("eph-token");
    const token = JSON.parse(tokenObj).value || localStorage.getItem("AccessToken");
    const requestBody = {
      expertID: expertID,
    };
    try {
      let modifiedUrl = "get-profile-language";
      const response = await fetch(`${API_URL}${modifiedUrl}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });
      const responseData = await response.json();
      if (responseData.status === "success") {
        const formatted = responseData.languages
          .map(lang => `${lang.languageName} (${lang.level})`)
          .join(", ");

        setLanguages(formatted);
        setLanguageList(responseData.languages || []);
        setFormData((prev) => ({
          ...prev,
          language: formatted,
        }));
      } else {
        console.error("Failed to get language");
      }
    } catch (error) {
      console.error("Error while calling the backend API:", error);
    }
  };

  useEffect(() => {
    getExpertExperience();
    fetchExpert();
    getLanguages();
  }, [expertID]);

  const dimension = [
    "Professional exp.",
    "Stakeholder mgmt.",
    "Project mgmt.",
    "SAP SD/MM",
    "Integration monitoring"
  ];

  const [selfAssessment, setSelfAssessment] = useState(
    dimension.reduce((acc, item) => {
      acc[item] = 5; // default value
      return acc;
    }, {})
  );


  const handleDragEnd = (result) => {
    setIsDragging(false);
    dragStartRef.current = false;

    if (!result.destination) return;

    const items = Array.from(profileExperience);
    const [movedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, movedItem);

    // Update UI state
    setProfileExperience(items);

    // Sync into formData as JSON
    setFormData(prev => ({
      ...prev,
      experience: items, // full reordered list
    }));
};

  const syncEducationToFormData = (updatedEducation) => {
    setProfileEducation(updatedEducation);

    setFormData(prev => ({
      ...prev,
      education: updatedEducation.join("\n\n"),
    }));
  };

  const handleInfoDone = () => {
    const editedLanguages = languageList
      .map((lang) => `${lang.languageName} (${lang.level})`)
      .join(", ");

    const locationString =
      [city, country].filter(Boolean).join(", ") ||
      region ||
      "No information";

    setFormData((prev) => {
      const isLocationChanged = prev.location !== locationString;
      const isLanguageChanged = prev.language !== editedLanguages;

      // Only call API if something changed
      if (isLocationChanged || isLanguageChanged) {
        saveInfoData({
          city,
          country,
          region,
          languages: languageList,
        });
      }

      return {
        ...prev,
        location: locationString,
        language: editedLanguages,
      };
    });

    setIsInfoEdit(false);
  };

  const [rateDraft, setRateDraft] = useState(() => ({
    currency:
      project?.cons_exp === "exp"
        ? formData.hourlyRateCurrency
        : formData.dailyRateCurrency,
    value: project?.cons_exp === "exp" ? formData.hourlyRate : formData.dailyRate,
  }));

  // Keep draft in sync when user switches expertID / project type / formData changes externally
  useEffect(() => {
    setRateDraft({
      currency:
        project?.cons_exp === "exp"
          ? formData.hourlyRateCurrency
          : formData.dailyRateCurrency,
      value:
        project?.cons_exp === "exp" ? formData.hourlyRate : formData.dailyRate,
    });
  }, [
    project?.cons_exp,
    formData.hourlyRateCurrency,
    formData.dailyRateCurrency,
    formData.hourlyRate,
    formData.dailyRate,
  ]);

  const handleRateDone = () => {
    const cleanValue =
      rateDraft.value === "" || rateDraft.value === null
        ? ""
        : Number(rateDraft.value);

    setFormData((prev) => {
      if (project?.cons_exp === "exp") {
        return {
          ...prev,
          hourlyRateCurrency: rateDraft.currency,
          hourlyRate: cleanValue,
        };
      }
      return {
        ...prev,
        dailyRateCurrency: rateDraft.currency,
        dailyRate: cleanValue,
      };
    });

    setIsRateEdit(false);
  };

  const IconWithTooltip = ({ label, children }) => (
    <span className="relative inline-flex items-center group">
      {children}

      {/* Tooltip */}
      <span
        className="
          pointer-events-none
          absolute left-0 top-1/2 -translate-y-1/2 translate-x-7
          whitespace-nowrap
          rounded-md bg-gray-900 text-white
          text-[11px] px-2 py-1
          opacity-0 group-hover:opacity-100
          transition-opacity duration-150
          shadow-lg
          z-50
        "
        role="tooltip"
      >
        {label}
        <span
          className="
            absolute left-[-6px] top-1/2 -translate-y-1/2
            w-0 h-0
            border-y-[6px] border-y-transparent
            border-r-[6px] border-r-gray-900
          "
        />
      </span>
    </span>
  );

  // Keep UI + formData in sync in one place
  const commitExperience = (next) => {
    setProfileExperience(next);
    setFormData((prev) => ({ ...prev, experience: next }));
  };

  const updateExpField = (expIndex, field, value) => {
    commitExperience(
      profileExperience.map((exp, i) =>
        i === expIndex ? { ...exp, [field]: value } : exp
      )
    );
  };

  const updateRoleField = (expIndex, roleIndex, field, value) => {
    commitExperience(
      profileExperience.map((exp, i) => {
        if (i !== expIndex) return exp;
        const roles = Array.isArray(exp.subExperienceBlocks) ? exp.subExperienceBlocks : [];
        const nextRoles = roles.map((r, j) => (j === roleIndex ? { ...r, [field]: value } : r));
        return { ...exp, subExperienceBlocks: nextRoles };
      })
    );
  };

  const deleteExperienceRow = (expIndex) => {
    commitExperience(profileExperience.filter((_, i) => i !== expIndex));
  };

  const deleteRoleRow = (expIndex, roleIndex) => {
    commitExperience(
      profileExperience.map((exp, i) => {
        if (i !== expIndex) return exp;
        const roles = Array.isArray(exp.subExperienceBlocks) ? exp.subExperienceBlocks : [];
        const nextRoles = roles.filter((_, j) => j !== roleIndex);
        return { ...exp, subExperienceBlocks: nextRoles };
      })
    );
  };

  const openLogoEditor = (expIndex) => {
    setActiveLogoExpIndex(expIndex);
    setTempCompanyLogo(profileExperience?.[expIndex]?.companyLogo || "");
    setIsLogoModalOpen(true);
  };

  const closeLogoEditor = () => {
    setIsLogoModalOpen(false);
    setActiveLogoExpIndex(null);
    setTempCompanyLogo("");
  };

  const saveCompanyLogo = () => {
    if (activeLogoExpIndex === null) return;

    commitExperience(
      profileExperience.map((exp, i) =>
        i === activeLogoExpIndex
          ? { ...exp, companyLogo: tempCompanyLogo || "" }
          : exp
      )
    );

    closeLogoEditor();
  };

  const removeCompanyLogo = () => {
    setTempCompanyLogo("");
  };

  const dataURItoBlob = (dataURI) => {
    const byteString = atob(dataURI.split(",")[1]);
    const arrayBuffer = new ArrayBuffer(byteString.length);
    const uint8Array = new Uint8Array(arrayBuffer);

    for (let i = 0; i < byteString.length; i++) {
      uint8Array[i] = byteString.charCodeAt(i);
    }

    return new Blob([arrayBuffer], { type: "image/png" });
  };

  const previewLogoFile = (file) => {
    if (!file) return;

    if (file.size > 50 * 1024) {
      Swal.fire({
        title: "File too large",
        text: "Only images up to 50KB are allowed.",
        icon: "warning",
        showConfirmButton: false,
        timer: 1500,
      });
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setTempCompanyLogo(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleHtmlImgUrl = async (imageURL) => {
    try {
      if (!imageURL) return;

      if (imageURL.includes("images.expertpowerhouse.com")) {
        setTempCompanyLogo(imageURL);
        return;
      }

      const res = await fetch(imageURL, { mode: "cors" });
      const blob = await res.blob();
      previewLogoFile(blob);
    } catch (err) {
      console.error("Could not load image from pasted content.", err);
    }
  };

  const handleLogoFiles = async (e) => {
    // File input
    if (e.target?.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      previewLogoFile(file);
      return;
    }

    // Native file drop
    if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      previewLogoFile(file);
      return;
    }

    // HTML-wrapped image drop
    const htmlData = e.dataTransfer?.getData?.("text/html");
    if (htmlData) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlData, "text/html");
      const imgElement = doc.querySelector("img");

      if (imgElement) {
        const imageURL = imgElement.getAttribute("src");

        try {
          if (imageURL.includes("images.expertpowerhouse.com")) {
            setTempCompanyLogo(imageURL);
            return;
          }

          if (imageURL.startsWith("data:image")) {
            const blob = dataURItoBlob(imageURL);
            previewLogoFile(new File([blob], "logo.png", { type: "image/png" }));
          } else {
            const res = await fetch(imageURL);
            if (!res.ok) throw new Error("Fetch failed");
            const blob = await res.blob();
            previewLogoFile(new File([blob], "logo.png", { type: "image/png" }));
          }
        } catch (err) {
          console.error("Could not load dropped image.", err);
        }

        return;
      }
    }

    console.warn("No valid image detected.");
  };

  const handleLogoPaste = async (e) => {
    const items = [...e.clipboardData.items];
    const imgItem = items.find((i) => i.type.indexOf("image") === 0);

    if (imgItem) {
      const file = imgItem.getAsFile();
      previewLogoFile(file);
      return;
    }

    const html = e.clipboardData.getData("text/html");
    if (html) {
      const doc = new DOMParser().parseFromString(html, "text/html");
      const img = doc.querySelector("img");

      if (img) {
        const src = img.getAttribute("src");

        if (src.startsWith("data:image")) {
          const blob = dataURItoBlob(src);
          previewLogoFile(new File([blob], "logo.png", { type: "image/png" }));
        } else {
          await handleHtmlImgUrl(src);
        }
        return;
      }
    }

    const text = e.clipboardData.getData("text/plain");
    if (text && /^https?:\/\//i.test(text)) {
      await handleHtmlImgUrl(text.trim());
      return;
    }

    console.warn("No image found in paste.");
  };

  const handleLogoDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    await handleLogoFiles(e);
  };

  const handleLogoDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const resolveLogoSrc = (logo) => {
    if (!logo) return industryLogo;

    if (
      logo.startsWith("data:image") ||
      logo.startsWith("blob:") ||
      logo.startsWith("http://") ||
      logo.startsWith("https://")
    ) {
      return logo;
    }

    if (logo.includes("/pictures/")) {
      return `https://images.expertpowerhouse.com${logo}`;
    }

    return logo;
  };

  // Experience modification
  const splitMonthYear = (dateStr) => {
    if (!dateStr) {
      return { month: "", year: "" };
    }

    const normalized = dateStr.trim();
    const parts = normalized.split(" ");

    return {
      month: parts[0] || "",
      year: parts[1] || "",
    };
  };

  const isCurrentDate = (dateStr) => {
    return !dateStr || CURRENT_VALUES.includes(dateStr.toLowerCase());
  };

  const getCurrentMonthYear = () => {
    const now = new Date();
    const month = now.toLocaleString("en-US", { month: "short" }); // Apr
    const year = String(now.getFullYear()); // 2026

    return { month, year };
  };

  const enrichExperienceDates = (experienceList = []) => {
    return experienceList.map((exp) => {
      const expIsCurrent = isCurrentDate(exp.leavingDate);
      const joining = splitMonthYear(exp.joiningDate);
      const leaving = splitMonthYear(exp.leavingDate);

      return {
        ...exp,
        joiningMonth: joining.month,
        joiningYear: joining.year,
        leavingMonth: expIsCurrent ? "" : leaving.month,
        leavingYear: expIsCurrent ? "" : leaving.year,
        isCurrent: expIsCurrent,
        previousLeavingMonth: expIsCurrent ? "" : leaving.month,
        previousLeavingYear: expIsCurrent ? "" : leaving.year,
        subExperienceBlocks: exp.subExperienceBlocks?.map((block) => {
          const blockIsCurrent = isCurrentDate(block.leavingDate);
          const blockJoining = splitMonthYear(block.joiningDate);
          const blockLeaving = splitMonthYear(block.leavingDate);

          return {
            ...block,
            joiningMonth: blockJoining.month,
            joiningYear: blockJoining.year,
            leavingMonth: blockIsCurrent ? "" : blockLeaving.month,
            leavingYear: blockIsCurrent ? "" : blockLeaving.year,
            isCurrent: blockIsCurrent,
            previousLeavingMonth: blockIsCurrent ? "" : blockLeaving.month,
            previousLeavingYear: blockIsCurrent ? "" : blockLeaving.year,
          };
        }) || [],
      };
    });
  };

  const buildDateString = (month, year) => {
    if (!month || !year) return "";
    return `${month} ${year}`;
  };

  const handleExperienceChange = (expIndex, field, value) => {
    const updateExperience = (experienceList) => {
      const updatedExperience = [...experienceList];

      const updatedExp = {
        ...updatedExperience[expIndex],
        [field]: value,
      };

      updatedExp.joiningDate = buildDateString(
        updatedExp.joiningMonth,
        updatedExp.joiningYear
      );

      if (updatedExp.isCurrent) {
        updatedExp.leavingMonth = "";
        updatedExp.leavingYear = "";
        updatedExp.leavingDate =
          updatedExp.leavingDate?.toLowerCase() === "heute" ? "Heute" : "Present";
      } else {
        updatedExp.leavingDate = buildDateString(
          updatedExp.leavingMonth,
          updatedExp.leavingYear
        );

        updatedExp.previousLeavingMonth = updatedExp.leavingMonth || "";
        updatedExp.previousLeavingYear = updatedExp.leavingYear || "";
      }

      updatedExp.duration = updatedExp.subExperienceBlocks?.length
        ? calculateOverallDuration(updatedExp.subExperienceBlocks)
        : calculateDuration(updatedExp.joiningDate, updatedExp.leavingDate);

      updatedExperience[expIndex] = updatedExp;
      return updatedExperience;
    };

    setFormData((prev) => ({
      ...prev,
      experience: updateExperience(prev.experience),
    }));

    setProfileExperience((prev) => updateExperience(prev));
  };

  const toggleExperienceCurrentStatus = (expIndex) => {
    const updateExperienceCurrent = (experienceList) => {
      const updatedExperience = [...experienceList];
      const currentExp = updatedExperience[expIndex];
      const willBeCurrent = !currentExp.isCurrent;

      const updatedExp = {
        ...currentExp,
        isCurrent: willBeCurrent,
      };

      if (willBeCurrent) {
        updatedExp.previousLeavingMonth =
          updatedExp.leavingMonth || updatedExp.previousLeavingMonth || "";
        updatedExp.previousLeavingYear =
          updatedExp.leavingYear || updatedExp.previousLeavingYear || "";

        updatedExp.leavingMonth = "";
        updatedExp.leavingYear = "";
        updatedExp.leavingDate =
          updatedExp.leavingDate?.toLowerCase() === "heute" ? "Heute" : "Present";
      } else {
        const { month: currentMonth, year: currentYear } = getCurrentMonthYear();

        const restoredLeavingMonth =
          updatedExp.previousLeavingMonth || currentExp.leavingMonth || currentMonth;

        const restoredLeavingYear =
          updatedExp.previousLeavingYear || currentExp.leavingYear || currentYear;

        updatedExp.leavingMonth = restoredLeavingMonth;
        updatedExp.leavingYear = restoredLeavingYear;
        updatedExp.leavingDate = buildDateString(
          restoredLeavingMonth,
          restoredLeavingYear
        );
      }

      updatedExp.joiningDate = buildDateString(
        updatedExp.joiningMonth,
        updatedExp.joiningYear
      );

      updatedExp.duration = updatedExp.subExperienceBlocks?.length
        ? calculateOverallDuration(updatedExp.subExperienceBlocks)
        : calculateDuration(updatedExp.joiningDate, updatedExp.leavingDate);

      updatedExperience[expIndex] = updatedExp;
      return updatedExperience;
    };

    setFormData((prev) => ({
      ...prev,
      experience: updateExperienceCurrent(prev.experience),
    }));

    setProfileExperience((prev) => updateExperienceCurrent(prev));
  };

  const handleSubChange = (expIndex, subIndex, field, value) => {
    const updateSubBlock = (experienceList) => {
      const updatedExperience = [...experienceList];
      const updatedSubBlocks = [
        ...(updatedExperience[expIndex].subExperienceBlocks || []),
      ];

      const updatedBlock = {
        ...updatedSubBlocks[subIndex],
        [field]: value,
      };

      updatedBlock.joiningDate = buildDateString(
        updatedBlock.joiningMonth,
        updatedBlock.joiningYear
      );

      if (updatedBlock.isCurrent) {
        updatedBlock.leavingMonth = "";
        updatedBlock.leavingYear = "";
        updatedBlock.leavingDate =
          updatedBlock.leavingDate?.toLowerCase() === "heute" ? "Heute" : "Present";
      } else {
        updatedBlock.leavingDate = buildDateString(
          updatedBlock.leavingMonth,
          updatedBlock.leavingYear
        );

        updatedBlock.previousLeavingMonth = updatedBlock.leavingMonth || "";
        updatedBlock.previousLeavingYear = updatedBlock.leavingYear || "";
      }

      updatedBlock.duration = calculateDuration(
        updatedBlock.joiningDate,
        updatedBlock.leavingDate
      );

      // first put updated block into the array
      updatedSubBlocks[subIndex] = updatedBlock;

      // then calculate parent duration from the updated array
      updatedExperience[expIndex] = {
        ...updatedExperience[expIndex],
        subExperienceBlocks: updatedSubBlocks,
        duration: calculateOverallDuration(updatedSubBlocks),
      };

      return updatedExperience;
    };

    setFormData((prev) => ({
      ...prev,
      experience: updateSubBlock(prev.experience),
    }));

    setProfileExperience((prev) => updateSubBlock(prev));
  };

  const updateSubCurrentBlock = (experienceList, expIndex, subIndex) => {
    const updatedExperience = [...experienceList];
    const updatedSubBlocks = [
      ...(updatedExperience[expIndex].subExperienceBlocks || []),
    ];

    const currentBlock = updatedSubBlocks[subIndex];
    const willBeCurrent = !currentBlock.isCurrent;

    const updatedBlock = {
      ...currentBlock,
      isCurrent: willBeCurrent,
    };

    if (willBeCurrent) {
      updatedBlock.previousLeavingMonth =
        updatedBlock.leavingMonth || updatedBlock.previousLeavingMonth || "";
      updatedBlock.previousLeavingYear =
        updatedBlock.leavingYear || updatedBlock.previousLeavingYear || "";

      updatedBlock.leavingMonth = "";
      updatedBlock.leavingYear = "";
      updatedBlock.leavingDate =
        updatedBlock.leavingDate?.toLowerCase() === "heute" ? "Heute" : "Present";
    } else {
      const { month: currentMonth, year: currentYear } = getCurrentMonthYear();

      const restoredLeavingMonth =
        updatedBlock.previousLeavingMonth || currentBlock.leavingMonth || currentMonth;

      const restoredLeavingYear =
        updatedBlock.previousLeavingYear || currentBlock.leavingYear || currentYear;

      updatedBlock.leavingMonth = restoredLeavingMonth;
      updatedBlock.leavingYear = restoredLeavingYear;
      updatedBlock.leavingDate = buildDateString(
        restoredLeavingMonth,
        restoredLeavingYear
      );
    }

    updatedBlock.joiningDate = buildDateString(
      updatedBlock.joiningMonth,
      updatedBlock.joiningYear
    );

    updatedBlock.duration = calculateDuration(
      updatedBlock.joiningDate,
      updatedBlock.leavingDate
    );

    updatedSubBlocks[subIndex] = updatedBlock;

    updatedExperience[expIndex] = {
      ...updatedExperience[expIndex],
      subExperienceBlocks: updatedSubBlocks,
      duration: calculateOverallDuration(updatedSubBlocks),
    };

    return updatedExperience;
  };

  const toggleSubCurrentStatus = (expIndex, subIndex) => {
    setFormData((prev) => ({
      ...prev,
      experience: updateSubCurrentBlock(prev.experience, expIndex, subIndex),
    }));

    setProfileExperience((prev) =>
      updateSubCurrentBlock(prev, expIndex, subIndex)
    );
  };

  const calculateDuration = (start, end) => {
    if (!start) return "";
    const today = new Date();
    if (!end || ["present", "heute"].includes(end.toLowerCase()))
      end = format(today, "MMM yyyy");
    const s = startOfMonth(parse(start, "MMM yyyy", new Date()));
    const e = endOfMonth(parse(end, "MMM yyyy", new Date()));
    const totalMonths = differenceInMonths(e, s) + 1;
    const years = Math.floor(totalMonths / 12);
    const months = totalMonths % 12;
    return `${years > 0 ? `${years} yr${years > 1 ? "s" : ""} ` : ""}${
      months > 0 ? `${months} mo${months > 1 ? "s" : ""}` : ""
    }`.trim();
  };

  const calculateOverallDuration = (blocks) => {
    const joinDates = blocks.map((b) =>
      parse(`${b.joiningMonth} ${b.joiningYear}`, "MMM yyyy", new Date())
    );
    const leaveDates = blocks.map((b) => {
      if (b.isCurrent) return new Date(); // now
      return parse(
        `${b.leavingMonth} ${b.leavingYear}`,
        "MMM yyyy",
        new Date()
      );
    });
    const earliest = new Date(Math.min(...joinDates));
    const latest = new Date(Math.max(...leaveDates));
    return calculateDuration(
      format(earliest, "MMM yyyy"),
      format(latest, "MMM yyyy")
    );
  };

  // Info modification
  const handleAddLanguage = () => {
    if (!newLanguage.languageName || !newLanguage.level) return setLevelMissing(true);
    setLanguageList((prev) => [...prev, newLanguage]);
    setNewLanguage({ languageName: "", level: "" });
    setLevelMissing(false);
  };

  const handleRemoveLanguage = (index) => {
    const removedLanguage = languageList[index];
    if (removedLanguage.ID) {
      deleteLanguage({
        language: removedLanguage
      });
    }
    setLanguageList((prev) => prev.filter((_, i) => i !== index));
  };

  // Api for saving rate data
  const saveInfoData = async (info) => {
    const tokenObj = localStorage.getItem("eph-token");
    const token = JSON.parse(tokenObj).value || localStorage.getItem("AccessToken");
    setIsSavingInfo(true);
    try {
      const response = await fetch(`${API_URL}save-eph-info-data`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          expertID: expertID,
          ...info,
        }),
      });

      const result = await response.json();

      if (result.status === "success") {
        console.log('language updated successfully');
      } else {
        console.log('Error while updating language');
      }
    } catch (error) {
      console.log('Unexpected error while updating language');
    } finally {
      setIsSavingInfo(false);
    }
  };

  const deleteLanguage = async (language) => {
    const tokenObj = localStorage.getItem("eph-token");
    const token = JSON.parse(tokenObj).value || localStorage.getItem("AccessToken");
    const requestBody = {
      expertID: expertID,
      ...language,
    };
    try {
      let modifiedUrl = "delete-expert-language";
      const response = await fetch(`${API_URL}${modifiedUrl}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });
      const responseData = await response.json();
      if (responseData.status === "success") {
        console.log('Successfully deleted language');
      } else {
        console.error("Failed to get language");
      }
    } catch (error) {
      console.error("Error while calling the backend API:", error);
    }
  };

  useEffect(() => {
    if (newLanguage.languageName && newLanguage.level) {
      setFlashAddLanguage(false);

      const start = setTimeout(() => {
        setFlashAddLanguage(true);
      }, 10);

      const stop = setTimeout(() => {
        setFlashAddLanguage(false);
      }, 900);

      return () => {
        clearTimeout(start);
        clearTimeout(stop);
      };
    } else {
      setFlashAddLanguage(false);
    }
  }, [newLanguage.languageName, newLanguage.level]);

  <style>
    {`
      @keyframes buttonFlash {
        0%, 100% {
          opacity: 1;
          transform: scale(1);
        }
        15% {
          opacity: 0.45;
          transform: scale(1.04);
        }
        30% {
          opacity: 1;
          transform: scale(1);
        }
        45% {
          opacity: 0.45;
          transform: scale(1.04);
        }
        60% {
          opacity: 1;
          transform: scale(1);
        }
      }
    `}
  </style>

  return (
    <>
      <div className="relative bg-white shadow-sm z-40">
        {/* EPH banner */}
        <div
          className="relative h-24 sm:h-40 bg-cover bg-top bg-[24%_0%] sm:bg-[23%_0%] rounded-t-[10px]"
          style={{ backgroundImage: `url(${bannerBack})` }}
        >
          <div className="absolute top-0 sm:top-4 sm:left-4 text-white text-xl p-2 rounded-full transition-transform duration-200 ease-in-out">
            <img
              src={logoWhite}
              alt="EPH Logo"
              className="w-[80px] sm:w-[180px]"
            />
          </div>
        </div>

        {/* Profile picture and name */}
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8">
          <div className="relative flex flex-col items-left text-left -mt-20 sm:-mt-24">
            {/* Top part of the menu bar */}
            <div className="mt-2 flex flex-col">
              {/* Left: Profile Picture */}
              <div className="flex flex-col">
                <div className="relative w-24 h-24 sm:w-36 sm:h-36 mt-3">
                  <ProfilePicture 
                    expertDefaultImg={expertDefaultImg}
                    profilePicturePreview={profilePicturePreview}
                    setProfilePicturePreview={setProfilePicturePreview}
                    expertID={expertID}
                    callingFrom="Application"
                  />
                </div>
              </div>
              
              <div className="flex flex-col md:flex-row
                justify-start md:justify-between
                items-start lg:items-center
                gap-4">

                <div className="flex flex-col">
                  <div className="flex flex-row items-center justify-start gap-2">
                    <p className="text-2xl sm:text-3xl font-bold text-gray-900 no-underline mb-1">{profileName}</p>
                    <button
                      type="button"
                      onClick={() => {
                        if (isInfoEdit) {
                          handleInfoDone(); // ✔ Save to formData
                        } else {
                          setIsInfoEdit(true); // Enter edit mode
                        }
                      }}
                      title={isInfoEdit ? "Save" : "Edit"}
                      className={`
                        flex items-center justify-center
                        w-8 h-8 rounded-full
                        border border-gray-300
                        transition-all duration-200
                        focus:outline-none
                        focus:border-[#365561]
                        focus:ring-2 focus:ring-[#365561]/40
                        ${
                          isInfoEdit
                            ? "bg-green-50 text-green-600 hover:bg-green-100"
                            : "bg-white text-gray-600 hover:bg-gray-100"
                        }
                      `}
                      disabled={isSavingInfo}
                    >
                      {isSavingInfo ? <i class="fa-solid fa-spinner fa-spin text-[#365561]"></i> : <i className={`fa-solid ${isInfoEdit ? "fa-check" : "fa-pen"} text-sm`} />}
                    </button>

                  </div>

                  {!isInfoEdit && (
                    <div className="flex flex-col gap-2 text-sm sm:text-md mb-1">
                      <div className="flex flex-col">
                        <div className="flex items-start gap-1 max-w-xs sm:max-w-sm md:max-w-md">
                          <IconWithTooltip label="Position / Role">
                            <span className="leading-5 cursor-help">💼</span>
                          </IconWithTooltip>
                          <span className="leading-5 break-words whitespace-normal">
                            {formData.position}
                          </span>
                        </div>
                        <div className="flex items-start gap-1 max-w-xs sm:max-w-sm md:max-w-md">
                          <IconWithTooltip label="Location">
                            <span className="leading-5 cursor-help">🌍</span>
                          </IconWithTooltip>
                          <span className="leading-5 break-words whitespace-normal">
                            {formData.location}
                          </span>
                        </div>
                        <div className="flex items-start gap-1 max-w-xs sm:max-w-sm md:max-w-md">
                          <IconWithTooltip label="Languages">
                            <span className="leading-5 cursor-help">🗣️</span>
                          </IconWithTooltip>
                          <span className="leading-5 break-words whitespace-normal">
                            {formData.language}
                          </span>
                        </div>
                        <div className="flex items-start gap-1 max-w-[400px]">
                          <IconWithTooltip label="Availability note for the project">
                            <span className="leading-5 cursor-help">📆</span>
                          </IconWithTooltip>
                          <span className="leading-5 break-words whitespace-normal">
                            {formData.availabilityComment ? formData.availabilityComment : 'Fully available'}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {isInfoEdit && (
                    <div className="flex flex-col gap-2 text-sm sm:text-md mb-1">

                      {/* POSITION */}
                      <div className="flex items-start gap-1">
                        <IconWithTooltip label="Position / Role">
                          <span className="leading-5 cursor-help">💼</span>
                        </IconWithTooltip>
                        <input
                          type="text"
                          value={formData.position || ""}
                          onChange={(e) => 
                            setFormData((prev) => ({
                              ...prev,
                              position: e.target.value,
                            }))
                          }
                          placeholder="Independent Consultant"
                          className="
                            leading-5 break-words
                            border-b border-gray-300 hover:border-[#365561]
                            focus:border-[#365561] focus:outline-none
                            pb-0.5 bg-transparent cursor-text
                            w-full min-w-0
                          "
                        />
                      </div>

                      {/* LOCATION */}
                      <div className="flex items-start gap-1">
                        <IconWithTooltip label="Location">
                          <span className="leading-5 cursor-help">🌍</span>
                        </IconWithTooltip>
                        <div className="flex gap-2">
                          <input
                            className="w-1/3 border-b border-gray-300 hover:border-[#365561] focus:border-[#365561] focus:outline-none pb-1"
                            placeholder="City"
                            value={city}
                            onChange={(e) => setCity(e.target.value)}
                          />
                          <div className="w-1/3">
                            <input
                              list="country-list"
                              className="w-full border-b border-gray-300 hover:border-[#365561] focus:border-[#365561] focus:outline-none pb-1"
                              placeholder="Country"
                              value={country}
                              onChange={(e) => setCountry(e.target.value)}
                            />
                            <datalist id="country-list">
                              {COUNTRY_LIST.map((c) => (
                                <option key={c} value={c} />
                              ))}
                            </datalist>
                          </div>

                          <div className="w-1/3">
                            <input
                              list="region-list"
                              className="w-full border-b border-gray-300 hover:border-[#365561] focus:border-[#365561] focus:outline-none pb-1"
                              placeholder="Region"
                              value={region}
                              onChange={(e) => setRegion(e.target.value)}
                            />
                            <datalist id="region-list">
                              {REGION_LIST.map((r) => (
                                <option key={r} value={r} />
                              ))}
                            </datalist>
                          </div>
                        </div>
                      </div>

                      {/* LANGUAGE */}
                      <div className="flex items-start gap-1 max-w-md">
                        <IconWithTooltip label="Languages">
                          <span className="leading-5 cursor-help">🗣️</span>
                        </IconWithTooltip>
                        <div className="flex flex-col gap-2 mb-3">
                          {languageList.map((l, i) => (
                            <div key={i} className="flex justify-between items-center gap-2">
                              {l.level ? <span>{l.languageName} ({l.level})</span> : <span>{l.languageName}</span>}
                              <button
                                className="text-red-500 hover:underline text-xs"
                                onClick={() => handleRemoveLanguage(i)}
                              >
                                Remove
                              </button>
                            </div>
                          ))}
                          <div className="flex gap-2">
                            <div className="w-1/2">
                              <input
                                list="language-options"
                                placeholder="Language"
                                value={newLanguage.languageName}
                                onChange={(e) => setNewLanguage({ ...newLanguage, languageName: e.target.value })}
                                className="w-full border-b border-gray-300 hover:border-[#365561] focus:border-[#365561] focus:outline-none text-sm pb-1 bg-white"
                              />
                              <datalist id="language-options">
                                {LANGUAGE_LIST.map((lang) => (
                                  <option key={lang} value={lang} />
                                ))}
                              </datalist>
                            </div>
                            <div className="w-2/6">
                              <input
                                list="language-levels"
                                placeholder="Level"
                                value={newLanguage.level}
                                onChange={(e) => setNewLanguage({ ...newLanguage, level: e.target.value })}
                                className="w-full border-b border-gray-300 hover:border-[#365561] focus:border-[#365561] focus:outline-none text-sm pb-1 bg-white"
                              />
                              <datalist id="language-levels">
                                {languageLevel.map((level) => (
                                  <option key={level} value={level} />
                                ))}
                              </datalist>
                            </div>
                          </div>
                          {newLanguage.languageName && (
                            <div className="flex flex-row items-center gap-1">
                              <button
                                type="button"
                                onClick={handleAddLanguage}
                                className={`
                                  rounded-full px-4 py-2 text-xs font-semibold transition-all duration-300 focus:outline-none
                                  ${
                                    newLanguage.languageName && newLanguage.level
                                      ? "bg-[#365561] text-white shadow-sm hover:opacity-95 active:scale-[0.98]"
                                      : "bg-gray-200 text-gray-500 cursor-not-allowed"
                                  }
                                  ${
                                    flashAddLanguage
                                      ? "animate-[buttonFlash_0.8s_ease-in-out_1]"
                                      : ""
                                  }
                                `}
                                style={
                                  flashAddLanguage
                                    ? {
                                        animationName: "buttonFlash",
                                        animationDuration: "0.8s",
                                        animationTimingFunction: "ease-in-out",
                                        animationIterationCount: 1,
                                      }
                                    : {}
                                }
                              >
                                + Add Language
                              </button>

                              {levelMissing && !newLanguage.level && (
                                <p className="text-red-500 text-xs">(select language level)</p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* AVAILABILITY */}
                      <div className="flex items-start gap-1 max-w-md">
                        <IconWithTooltip label="Availability note for the project">
                          <span className="leading-5 cursor-help">📆</span>
                        </IconWithTooltip>
                        <input
                          type="text"
                          value={formData.availabilityComment || ""}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              availabilityComment: e.target.value,
                            }))
                          }
                          placeholder="Fully available"
                          className="
                            leading-5 break-words
                            border-b border-gray-300 hover:border-[#365561]
                            focus:border-[#365561] focus:outline-none
                            pb-0.5 bg-transparent cursor-text
                            w-full min-w-0
                          "
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* RIGHT: Rate Card */}
                <div className="flex justify-start md:justify-end mb-4">
                  <div className="relative bg-[#365561] text-white rounded-xl px-6 py-4 shadow-lg min-w-[200px]">
                    {/* Edit / Done button */}
                    <button
                      type="button"
                      onClick={() => {
                        if (isRateEdit) {
                          handleRateDone(); // save into formData
                        } else {
                          // load current values into draft when entering edit mode
                          setRateDraft({
                            currency:
                              project?.cons_exp === "exp"
                                ? formData.hourlyRateCurrency
                                : formData.dailyRateCurrency,
                            value:
                              project?.cons_exp === "exp"
                                ? formData.hourlyRate
                                : formData.dailyRate,
                          });
                          setIsRateEdit(true);
                        }
                      }}
                      title={isRateEdit ? "Save" : "Edit"}
                      className={`
                        absolute top-2 right-2
                        flex items-center justify-center
                        w-8 h-8 rounded-full
                        border border-gray-300
                        transition-all duration-200
                        focus:outline-none
                        focus:border-[#365561]
                        focus:ring-2 focus:ring-[#365561]/40
                        ${
                          isRateEdit
                            ? "bg-green-50 text-green-600 hover:bg-green-100"
                            : "bg-white text-gray-600 hover:bg-gray-100"
                        }
                      `}
                    >
                      <i className={`fa-solid ${isRateEdit ? "fa-check" : "fa-pen"} text-sm`} />
                    </button>

                    <div className="text-[10px] sm:text-xs uppercase tracking-wide opacity-80">
                      rate
                    </div>

                    {/* VIEW MODE */}
                    {!isRateEdit && (
                      <>
                        <div className="mt-1 text-xl sm:text-3xl font-bold leading-tight">
                          {rateCurrency} {rateValue}
                        </div>
                        <div className="text-[10px] sm:text-xs opacity-80">{rateFrequency}</div>
                      </>
                    )}

                    {/* EDIT MODE */}
                    {isRateEdit && (
                      <div className="mt-0.5 flex flex-col gap-2">
                        <div className="flex gap-2 items-center">
                          {/* Currency dropdown */}
                          <div className="relative w-[70px]">
                            <select
                              value={rateDraft.currency ?? "EUR"}
                              onChange={(e) =>
                                setRateDraft((p) => ({ ...p, currency: e.target.value }))
                              }
                              className="
                                appearance-none w-full bg-white
                                text-gray-800 text-sm
                                rounded-md py-1 pr-8 pl-2
                                outline-none border border-gray-200
                                focus:border-[#365561]
                                focus:shadow-[0_0_8px_rgba(54,85,97,0.4)]
                                cursor-pointer
                              "
                            >
                              {currencies.map((c) => (
                                <option key={c} value={c}>
                                  {c}
                                </option>
                              ))}
                            </select>

                            <i className="fa-solid fa-chevron-down pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-gray-400" />
                          </div>

                          {/* Amount */}
                          <input
                            type="number"
                            value={rateDraft.value ?? ""}
                            onChange={(e) =>
                              setRateDraft((p) => ({ ...p, value: e.target.value }))
                            }
                            className="
                              flex-1
                              text-sm text-gray-800
                              bg-white rounded-md px-2 py-1
                              outline-none border border-gray-200
                              focus:border-[#365561]
                              focus:shadow-[0_0_8px_rgba(54,85,97,0.4)]
                              w-[60px]
                            "
                            placeholder="0"
                          />
                        </div>

                        <div className="text-[10px] sm:text-xs opacity-80">{rateFrequency}</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 pt-4 sm:pt-36 pb-10 grid grid-cols-1 lg:grid-cols-6 gap-6">
        <section className="col-span-3 space-y-6">
          {/* EPH summary */}
          <div className="bg-white rounded-xl p-6 relative transition-all duration-300 shadow">
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-xl font-bold">EPH Summary</h2>

              {/* Edit / Done button */}
              <button
                type="button"
                onClick={() => setIsSummaryEdit(!isSummaryEdit)}
                title={isSummaryEdit ? "Done" : "Edit"}
                className={`
                  flex items-center justify-center
                  w-8 h-8 rounded-full
                  border border-gray-300
                  transition-all duration-200
                  focus:outline-none
                  focus:border-[#365561]
                  focus:ring-2 focus:ring-[#365561]/40
                  ${
                    isSummaryEdit
                      ? "bg-green-50 text-green-600 hover:bg-green-100"
                      : "bg-white text-gray-600 hover:bg-gray-100"
                  }
                `}
              >
                <i
                  className={`fa-solid ${
                    isSummaryEdit ? "fa-check" : "fa-pen"
                  } text-sm`}
                />
              </button>
            </div>

            {/* ================= VIEW MODE ================= */}
            {!isSummaryEdit && (
              <div
                className="prose prose-sm text-gray-700 text-sm leading-relaxed
                          [&_h3]:text-md [&_h3]:mt-2 [&_h3]:font-semibold
                          [&_ul]:list-disc [&_ul]:pl-5
                          [&_li]:mb-1"
                dangerouslySetInnerHTML={{ __html: profileSummary }}
              />
            )}

            {/* ================= EDIT MODE ================= */}
            {isSummaryEdit && (
              <div className="flex flex-col gap-3">
                {/* INTRO */}
                <div
                  contentEditable
                  suppressContentEditableWarning
                  className="w-full min-h-[80px] text-sm text-gray-700 leading-relaxed
                            outline-none border border-gray-300 rounded-md p-2
                            focus:border-blue-400 focus:shadow-[0_0_8px_rgba(54,85,97,0.4)]"
                  onBlur={(e) => {
                    const value = e.target.innerText;
                    setFormData((prev) => ({
                      ...prev,
                      question1: value,
                    }));
                  }}
                >
                  {formData.question1}
                </div>

                {/* HEADING */}
                <h3 className="text-md font-semibold">
                  Relevant project experience
                </h3>

                {/* PROJECT LIST */}
                <ul className="list-disc pl-4 space-y-1">
                  {formData.question2.map((exp, index) => (
                    <li
                      key={index}
                      contentEditable
                      suppressContentEditableWarning
                      className="text-sm text-gray-700 outline-none
                                border border-gray-300 rounded-md p-1
                                focus:border-blue-400 focus:shadow-[0_0_6px_rgba(54,85,97,0.4)]"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") e.preventDefault();
                      }}
                      onBlur={(e) => {
                        const updated = [...formData.question2];
                        updated[index] = e.target.innerText;

                        setFormData((prev) => ({
                          ...prev,
                          question2: updated,
                        }));
                      }}
                    >
                      {exp}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* EPH matching */}
          {/* <div className="bg-white rounded-xl p-6 relative transition-all duration-300 shadow">
            <h2 className="text-xl font-bold mb-4">Self Assessment</h2>

            <ul className="space-y-3">
              {dimension.map((dim) => (
                <li
                  key={dim}
                  className="flex items-center justify-between gap-4"
                >
                  <span className="text-sm text-gray-800 w-40">
                    {dim}
                  </span>

                  <div className="flex items-center gap-3 flex-1">
                    <input
                      type="range"
                      min="1"
                      max="5"
                      step="1"
                      value={selfAssessment[dim]}
                      onChange={(e) =>
                        setSelfAssessment((prev) => ({
                          ...prev,
                          [dim]: Number(e.target.value),
                        }))
                      }
                      className="
                        w-full
                        accent-[#365561]
                        cursor-pointer
                      "
                    />
                    <span className="text-sm font-medium text-gray-700 w-6 text-right">
                      {selfAssessment[dim]}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </div> */}

        </section>
        <aside className="col-span-3 space-y-6">
          {/* Experience section */}
          <motion.div
            key="experience-view"
            layout
            initial={{ x: 0, opacity: 1 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: "100%", opacity: 0 }}
            transition={{ type: "spring", stiffness: 500, damping: 30 }}
            className="bg-white rounded-xl shadow p-6 relative"
          >
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-xl font-bold mb-6">Experience</h2>
              {/* Edit / Done button */}
              <button
                type="button"
                onClick={() => setIsExperienceEdit(!isExperienceEdit)}
                title={isExperienceEdit ? "Done" : "Edit"}
                className={`
                  flex items-center justify-center
                  w-8 h-8 rounded-full
                  border border-gray-300
                  transition-all duration-200
                  focus:outline-none
                  focus:border-[#365561]
                  focus:ring-2 focus:ring-[#365561]/40
                  ${
                    isExperienceEdit
                      ? "bg-green-50 text-green-600 hover:bg-green-100"
                      : "bg-white text-gray-600 hover:bg-gray-100"
                  }
                `}
              >
                <i
                  className={`fa-solid ${
                    isExperienceEdit ? "fa-check" : "fa-pen"
                  } text-sm`}
                />
              </button>
            </div>

            {/* {profileExperience?.length > 0 ? ( */}
            {formData.experience?.length > 0 ? (
              <DragDropContext
                onDragStart={() => {
                  setIsDragging(true);
                  dragStartRef.current = true;
                  clearTimeout(clickTimeout.current);
                }}
                onDragEnd={handleDragEnd}
              >
                <Droppable droppableId="experience-list">
                  {(provided) => (
                    <div
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      className="space-y-2"
                    >
                      {/* {profileExperience.map((exp, index) => ( */}
                      {formData.experience.map((exp, index) => (
                        <Draggable
                          key={exp.id || index}
                          draggableId={exp.id?.toString() || `exp-${index}`}
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps} // drag from anywhere
                              className={`relative w-full rounded-md transition duration-200 px-2 py-1 select-none group ${
                                snapshot.isDragging
                                  ? "bg-gray-200 shadow-md cursor-grabbing"
                                  : "cursor-grab hover:bg-gray-100"
                              }`}
                            >
                              {isExperienceEdit && (
                                <button
                                  type="button"
                                  title="Delete this experience"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    deleteExperienceRow(index);
                                  }}
                                  className="absolute top-2 right-2 text-gray-400 hover:text-red-500"
                                >
                                  <i className="fa-solid fa-trash" />
                                </button>
                              )}

                              {exp.subExperienceBlocks?.length > 0 ? (
                                <>
                                  <div className="flex ml-1 items-start gap-4">
                                    {isExperienceEdit ? (
                                      <div className="relative group">
                                        {exp.companyLogo ? (
                                          <img
                                            src={resolveLogoSrc(exp.companyLogo)}
                                            alt="Company Logo"
                                            onError={(e) => {
                                              e.target.onerror = null; // Prevent infinite loop
                                              e.target.src = industryLogo;
                                            }}
                                            className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-contain border"
                                          />
                                        ) : (
                                          <img
                                            src={industryLogo}
                                            alt="Default Industry Logo"
                                            className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-contain border bg-gray-100"
                                          />
                                        )}
                                        <div
                                          className="absolute inset-0 flex items-center justify-center rounded-full bg-black bg-opacity-40 text-white text-[9px] font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300 cursor-pointer"
                                          onClick={() => openLogoEditor(index)}
                                        >
                                          <span className="text-center font-semibold">Click <br/>to edit</span>
                                        </div>
                                      </div>
                                    ) : (
                                      <>
                                        {exp.companyLogo ? (
                                          <img
                                            src={resolveLogoSrc(exp.companyLogo)}
                                            alt="Company Logo"
                                            onError={(e) => {
                                              e.target.onerror = null; // Prevent infinite loop
                                              e.target.src = industryLogo;
                                            }}
                                            className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-contain border"
                                          />
                                        ) : (
                                          <img
                                            src={industryLogo}
                                            alt="Default Industry Logo"
                                            className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-contain border bg-gray-100"
                                          />
                                        )}
                                      </>
                                    )}

                                    {isExperienceEdit ? (
                                      <div className="space-y-1">
                                        <div
                                          contentEditable
                                          suppressContentEditableWarning
                                          className="border-b border-gray-300 hover:border-[#365561] focus:border-[#365561] focus:outline-none font-semibold text-gray-900 text-sm sm:text-base pb-0.1 pl-1 bg-transparent cursor-text"
                                          onBlur={(e) => updateExpField(index, "companyName", e.target.innerText.trim())}
                                        >
                                          {exp.companyName}
                                        </div>

                                        <div
                                          className="cursor-text select-text text-sm text-gray-500 outline-none rounded px-2 py-1 focus:border-[#365561] focus:shadow-[0_0_6px_rgba(54,85,97,0.3)]"
                                        >
                                          <p>{calculateOverallDuration(exp.subExperienceBlocks)}</p> 
                                        </div>
                                      </div>
                                    ) : (
                                      <div>
                                        <h3 className="font-semibold text-gray-900 text-sm sm:text-base">{exp.companyName}</h3>
                                        <p className="text-sm text-gray-500">{exp.duration}</p>
                                      </div>
                                    )}

                                  </div>
                                  <ul className="list-none mt-2 ml-9 pl-4 relative">
                                    {exp.subExperienceBlocks.map((role, j) => (
                                      <li key={j} className={`relative pl-2 ${j === exp.subExperienceBlocks.length - 1 ? "" : "mb-3"}`}>

                                        {isExperienceEdit && exp.subExperienceBlocks.length > 2 && (
                                          <button
                                            type="button"
                                            title="Delete this role"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              e.preventDefault();
                                              deleteRoleRow(index, j);
                                            }}
                                            className="absolute right-0 top-0 text-gray-400 hover:text-red-500"
                                          >
                                            <i className="fa-solid fa-trash" />
                                          </button>
                                        )}


                                        <div className="absolute -left-[35px] top-2 w-2 h-2 bg-gray-400 rounded-full z-10"></div>
                                        {j !== exp.subExperienceBlocks.length - 1 && (
                                          <div className="absolute -left-[32px] top-5 h-full w-px bg-gray-300 z-0"></div>
                                        )}

                                        {isExperienceEdit ? (
                                          <div
                                            contentEditable
                                            suppressContentEditableWarning
                                            className="border-b border-gray-300 hover:border-[#365561] focus:border-[#365561] focus:outline-none text-sm pb-0.1 pl-1 bg-transparent cursor-text"
                                            onBlur={(e) => updateRoleField(index, j, "designation", e.target.innerText.trim())}
                                          >
                                            {role.designation}
                                          </div>
                                        ) : (
                                          <h4 className="text-gray-800 font-medium text-sm sm:text-base">{role.designation}</h4>
                                        )}
                                        <div className="text-xs text-gray-500 flex flex-wrap gap-2">
                                          {isExperienceEdit ? (
                                            <div className="flex flex-col">
                                              <div className="flex gap-2 mb-2">
                                                <select
                                                  className="border-b border-gray-300 hover:border-[#365561] focus:border-[#365561] focus:outline-none text-sm pb-0.1 bg-transparent cursor-pointer"
                                                  value={role.joiningMonth}
                                                  onChange={(e) =>
                                                    handleSubChange(index, j, "joiningMonth", e.target.value)
                                                  }
                                                >
                                                  <option value="">Month</option>
                                                  {MONTHS.map((m) => (
                                                    <option key={m} value={m}>
                                                      {m}
                                                    </option>
                                                  ))}
                                                </select>
                                                <select
                                                  className="border-b border-gray-300 hover:border-[#365561] focus:border-[#365561] focus:outline-none text-sm pb-0.1 bg-transparent cursor-pointer"
                                                  value={role.joiningYear}
                                                  onChange={(e) =>
                                                    handleSubChange(index, j, "joiningYear", e.target.value)
                                                  }
                                                >
                                                  <option value="">Year</option>
                                                  {YEARS.map((y) => (
                                                    <option key={y} value={y}>
                                                      {y}
                                                    </option>
                                                  ))}
                                                </select>
                                                <span className="self-center">-</span>
                                                {!role.isCurrent ? (
                                                  <>
                                                    <select
                                                      className="border-b border-gray-300 hover:border-[#365561] focus:border-[#365561] focus:outline-none text-sm pb-0.1 bg-transparent cursor-pointer"
                                                      value={role.leavingMonth}
                                                      onChange={(e) =>
                                                        handleSubChange(
                                                          index,
                                                          j,
                                                          "leavingMonth",
                                                          e.target.value
                                                        )
                                                      }
                                                    >
                                                      <option value="">Month</option>
                                                      {MONTHS.map((m) => (
                                                        <option key={m} value={m}>
                                                          {m}
                                                        </option>
                                                      ))}
                                                    </select>
                                                    <select
                                                      className="border-b border-gray-300 hover:border-[#365561] focus:border-[#365561] focus:outline-none text-sm pb-0.1 bg-transparent cursor-pointer"
                                                      value={role.leavingYear}
                                                      onChange={(e) =>
                                                        handleSubChange(
                                                          index,
                                                          j,
                                                          "leavingYear",
                                                          e.target.value
                                                        )
                                                      }
                                                    >
                                                      <option value="">Year</option>
                                                      {YEARS.map((y) => (
                                                        <option key={y} value={y}>
                                                          {y}
                                                        </option>
                                                      ))}
                                                    </select>
                                                  </>
                                                ) : (
                                                  <span className="self-center text-gray-400">
                                                    Present
                                                  </span>
                                                )}
                                              </div>

                                              <div className="flex items-center text-sm text-gray-600 mb-2">
                                                <input
                                                  type="checkbox"
                                                  checked={role.isCurrent}
                                                  onChange={() => toggleSubCurrentStatus(index, j)}
                                                  className="mr-2 cursor-pointer"
                                                />
                                                <span>Current Position</span>
                                              </div>

                                              <p className="text-xs text-gray-500">
                                                {calculateDuration(
                                                  `${role.joiningMonth} ${role.joiningYear}`,
                                                  role.isCurrent
                                                    ? "Present"
                                                    : `${role.leavingMonth} ${role.leavingYear}`
                                                )}
                                              </p>
                                            </div>

                                          ) : (
                                            <p className="text-xs text-gray-500">
                                              {role.joiningDate} {role.leavingDate ? "-" : null} {role.leavingDate} {role.duration ? "·" : null} {role.duration}
                                            </p>
                                          )}
                                        </div>
                                      </li>
                                    ))}
                                  </ul>
                                </>
                              ) : (
                                <div className="ml-1 flex items-start  gap-4">
                                  {isExperienceEdit ? (
                                    <div className="relative group">
                                      {exp.companyLogo ? (
                                        <img
                                          src={resolveLogoSrc(exp.companyLogo)}
                                          alt="Company Logo"
                                          onError={(e) => {
                                            e.target.onerror = null; // Prevent infinite loop
                                            e.target.src = industryLogo;
                                          }}
                                          className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-contain border"
                                        />
                                      ) : (
                                        <img
                                          src={industryLogo}
                                          alt="Default Industry Logo"
                                          className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-contain border bg-gray-100"
                                        />
                                      )}
                                      <div
                                        className="absolute inset-0 flex items-center justify-center rounded-full bg-black bg-opacity-40 text-white text-[9px] font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300 cursor-pointer"
                                        onClick={() => openLogoEditor(index)}
                                      >
                                        <span className="text-center font-semibold">Click <br/>to edit</span>
                                      </div>
                                    </div>
                                  ) : (
                                    <>
                                      {exp.companyLogo ? (
                                        <img
                                          src={resolveLogoSrc(exp.companyLogo)}
                                          alt="Company Logo"
                                          onError={(e) => {
                                            e.target.onerror = null; // Prevent infinite loop
                                            e.target.src = industryLogo;
                                          }}
                                          className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-contain border"
                                        />
                                      ) : (
                                        <img
                                          src={industryLogo}
                                          alt="Default Industry Logo"
                                          className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-contain border bg-gray-100"
                                        />
                                      )}
                                    </>
                                  )}
                                  <div className="flex-1 space-y-2">
                                    {/* Designation */}
                                    {isExperienceEdit ? (
                                      <div
                                        contentEditable
                                        suppressContentEditableWarning
                                        className="border-b border-gray-300 hover:border-[#365561] focus:border-[#365561] focus:outline-none text-gray-800 font-medium text-sm sm:text-base pb-0.1 pl-1 bg-transparent cursor-text"
                                        onBlur={(e) =>
                                          updateExpField(index, "designation", e.target.innerText.trim())
                                        }
                                      >
                                        {exp.designation || ""}
                                      </div>
                                    ) : (
                                      <h4 className="text-gray-800 font-medium text-sm sm:text-base">
                                        {exp.designation}
                                      </h4>
                                    )}

                                    {/* Company name */}
                                    {isExperienceEdit ? (
                                      <div
                                        contentEditable
                                        suppressContentEditableWarning
                                        className="border-b border-gray-300 hover:border-[#365561] focus:border-[#365561] focus:outline-none text-sm pb-0.1 pl-1 bg-transparent cursor-text"
                                        onBlur={(e) =>
                                          updateExpField(index, "companyName", e.target.innerText.trim())
                                        }
                                      >
                                        {exp.companyName || ""}
                                      </div>
                                    ) : (
                                      <p className="text-sm text-gray-500">{exp.companyName}</p>
                                    )}

                                    {/* Dates / Duration */}
                                    {isExperienceEdit ? (
                                      <>
                                        <div className="flex flex-col">
                                          <div className="flex gap-2 mb-2">
                                            <select
                                              className="border-b border-gray-300 hover:border-[#365561] focus:border-[#365561] focus:outline-none text-sm pb-0.1 bg-transparent cursor-pointer"
                                              value={exp.joiningMonth}
                                              onChange={(e) =>
                                                handleExperienceChange(index, "joiningMonth", e.target.value)
                                              }
                                            >
                                              <option value="">Month</option>
                                              {MONTHS.map((m) => (
                                                <option key={m} value={m}>
                                                  {m}
                                                </option>
                                              ))}
                                            </select>
                                            <select
                                              className="border-b border-gray-300 hover:border-[#365561] focus:border-[#365561] focus:outline-none text-sm pb-0.1 bg-transparent cursor-pointer"
                                              value={exp.joiningYear}
                                              onChange={(e) =>
                                                handleExperienceChange(index, "joiningYear", e.target.value)
                                              }
                                            >
                                              <option value="">Year</option>
                                              {YEARS.map((y) => (
                                                <option key={y} value={y}>
                                                  {y}
                                                </option>
                                              ))}
                                            </select>
                                            <span className="self-center">-</span>
                                            {!exp.isCurrent ? (
                                              <>
                                                <select
                                                  className="border-b border-gray-300 hover:border-[#365561] focus:border-[#365561] focus:outline-none text-sm pb-0.1 bg-transparent cursor-pointer"
                                                  value={exp.leavingMonth}
                                                  onChange={(e) =>
                                                    handleExperienceChange(
                                                      index,
                                                      "leavingMonth",
                                                      e.target.value
                                                    )
                                                  }
                                                >
                                                  <option value="">Month</option>
                                                  {MONTHS.map((m) => (
                                                    <option key={m} value={m}>
                                                      {m}
                                                    </option>
                                                  ))}
                                                </select>
                                                <select
                                                  className="border-b border-gray-300 hover:border-[#365561] focus:border-[#365561] focus:outline-none text-sm pb-0.1 bg-transparent cursor-pointer"
                                                  value={exp.leavingYear}
                                                  onChange={(e) =>
                                                    handleExperienceChange(
                                                      index,
                                                      "leavingYear",
                                                      e.target.value
                                                    )
                                                  }
                                                >
                                                  <option value="">Year</option>
                                                  {YEARS.map((y) => (
                                                    <option key={y} value={y}>
                                                      {y}
                                                    </option>
                                                  ))}
                                                </select>
                                              </>
                                            ) : (
                                              <span className="self-center text-gray-400">
                                                Present
                                              </span>
                                            )}
                                          </div>

                                          <div className="flex items-center text-sm text-gray-600 mb-2">
                                            <input
                                              type="checkbox"
                                              checked={exp.isCurrent}
                                              onChange={() => toggleExperienceCurrentStatus(index)}
                                              className="mr-2 cursor-pointer"
                                            />
                                            <span>Current Position</span>
                                          </div>

                                          <p className="text-xs text-gray-500">
                                            {calculateDuration(
                                              `${exp.joiningMonth} ${exp.joiningYear}`,
                                              exp.isCurrent
                                                ? "Present"
                                                : `${exp.leavingMonth} ${exp.leavingYear}`
                                            )}
                                          </p>
                                        </div>
                                      </>
                                    ) : (
                                      <p className="text-xs text-gray-500">
                                        {exp.joiningDate} {exp.leavingDate ? "-" : null} {exp.leavingDate}{" "}
                                        {exp.duration ? "·" : null} {exp.duration}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              )}

                              {/* Hover hint */}
                              <div className="absolute bottom-1 right-2 text-[10px] text-white bg-[#365561] rounded px-2 py-0.5 rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                                Drag to rearrange
                              </div>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            ) : (
              <p className="text-sm text-gray-500">
                No experience data available.
              </p>
            )}
          </motion.div>

          {/* Education section */}
          <div className="bg-white rounded-xl p-6 relative transition-all duration-300 shadow">
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-xl font-bold">Education</h2>

              <button
                type="button"
                onClick={() => setIsEducationEdit(!isEducationEdit)}
                title={isEducationEdit ? "Done" : "Edit"}
                className={`
                  flex items-center justify-center
                  w-8 h-8 rounded-full
                  border border-gray-300
                  transition-all duration-200
                  focus:outline-none
                  focus:border-[#365561]
                  focus:ring-2 focus:ring-[#365561]/40
                  ${
                    isEducationEdit
                      ? "bg-green-50 text-green-600 hover:bg-green-100"
                      : "bg-white text-gray-600 hover:bg-gray-100"
                  }
                `}
              >
                <i className={`fa-solid ${isEducationEdit ? "fa-check" : "fa-pen"} text-sm`} />
              </button>
            </div>

            {/* ================= VIEW MODE ================= */}
            {!isEducationEdit && (
              <div className="space-y-4">
                {profileEducation.map((block, index) => {
                  const lines = block.split("\n").filter(Boolean);

                  return (
                    <div key={index}>
                      <h4 className="text-gray-800 font-medium text-sm sm:text-base">
                        {lines[0]}
                      </h4>

                      {lines.slice(1).length > 0 && (
                        <p className="text-sm text-gray-500">
                          {lines.slice(1).join(" · ")}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* ================= EDIT MODE ================= */}
            {isEducationEdit && (
              <div className="space-y-4">
                {profileEducation.map((block, index) => (
                  <div
                    key={index}
                    contentEditable
                    suppressContentEditableWarning
                    className="
                      whitespace-pre-line
                      text-sm text-gray-700
                      border border-gray-300 rounded-md p-2
                      outline-none
                      focus:border-[#365561]
                      focus:shadow-[0_0_8px_rgba(54,85,97,0.4)]
                    "
                    onBlur={(e) => {
                      const updated = [...profileEducation];
                      updated[index] = e.target.innerText.trim();

                      syncEducationToFormData(updated);
                    }}
                  >
                    {block}
                  </div>
                ))}
              </div>
            )}
          </div>

        </aside>
      </div>

      <AnimatePresence>
        {isLogoModalOpen && (
          <motion.div
            className="fixed inset-0 bg-black/60 flex justify-center items-center z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeLogoEditor}
          >
            <motion.div
              className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 relative max-w-md w-[95%]"
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={closeLogoEditor}
                className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700"
              >
                <i className="fa-solid fa-xmark text-lg"></i>
              </button>

              <h2 className="text-xl font-semibold mb-2 text-center">
                Update Company Logo
              </h2>

              <p className="text-sm text-gray-500 text-center mb-5">
                Upload, drag & drop, or paste a company logo.
              </p>

              <div
                ref={logoDropArea}
                tabIndex={0}
                onMouseEnter={() => logoDropArea.current?.focus()}
                onDragOver={handleLogoDragOver}
                onDrop={handleLogoDrop}
                onPaste={handleLogoPaste}
                className="relative w-full min-h-[220px] rounded-2xl border border-gray-200 bg-gray-50 flex flex-col items-center justify-center p-6 text-center outline-none focus:ring-2 focus:ring-blue-200"
              >
                {tempCompanyLogo ? (
                  <img
                    src={resolveLogoSrc(tempCompanyLogo)}
                    alt="Logo preview"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = industryLogo;
                    }}
                    className="w-28 h-28 object-contain rounded-xl border bg-white p-2 shadow-sm"
                  />
                ) : (
                  <>
                    <div className="w-20 h-20 rounded-2xl bg-white border flex items-center justify-center mb-3">
                      <i className="fa-regular fa-image text-2xl text-gray-400"></i>
                    </div>
                    <p className="text-sm font-medium text-gray-700">
                      Drag & drop logo here
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Or paste an image / image URL / upload from device
                    </p>
                  </>
                )}
              </div>

              <div className="mt-5 text-center">
                <button
                  type="button"
                  onClick={() => document.getElementById("companyLogoFileInput")?.click()}
                  className="px-4 py-2 rounded-lg bg-white border border-gray-300 hover:bg-gray-100 text-sm font-medium text-gray-700"
                >
                  Browse file...
                </button>

                <input
                  id="companyLogoFileInput"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleLogoFiles}
                />
              </div>

              <div className="flex flex-col sm:flex-row justify-center gap-3 mt-6">
                <button
                  onClick={removeCompanyLogo}
                  className="px-4 py-2 rounded-lg bg-red-100 hover:bg-red-200 text-red-600"
                >
                  Remove
                </button>
                <button
                  onClick={closeLogoEditor}
                  className="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={saveCompanyLogo}
                  className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Save
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export function AvailabilityCalendar({
  setAvailabilityForm,
  availability = [],
  timezone,
  setTimezone,
  handleSetAvailability,
  embeddedWizardMode = false,
}) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [mobileDate, setMobileDate] = useState(new Date());
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionStart, setSelectionStart] = useState(null);
  const [selectionEnd, setSelectionEnd] = useState(null);
  const desktopScrollRef = useRef(null);
  const mobileSlotsRef = useRef(null);

  const START_HOUR = 9;
  const END_HOUR = 18;
  const ROW_HEIGHT = embeddedWizardMode ? 40 : 44;
  const hours = Array.from({ length: 24 }, (_, hour) => hour);
  const weekDates = getWeekDates(currentDate);

  function getWeekDates(date) {
    const week = [];
    const start = new Date(date);
    start.setHours(12, 0, 0, 0);
    start.setDate(start.getDate() - start.getDay());

    for (let index = 0; index < 7; index++) {
      const day = new Date(start);
      day.setDate(start.getDate() + index);
      week.push(day);
    }

    return week;
  }

  // Encode the exact calendar wall-clock value as UTC components.
  //
  // Example:
  // Calendar selection: 2026-07-29 09:00
  // Stored timestamp:   Date.UTC(2026, 6, 29, 9, 0, 0)
  // Separate timezone:  timezone
  //
  // The timezone is metadata only here. It must not shift the selected hour.
  const getSlotTimestamp = (date, hour) =>
    Math.floor(
      Date.UTC(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        hour,
        0,
        0,
        0
      ) / 1000
    );

  const timestampToLocalWallClock = (timestamp) => {
    const encodedDate = new Date(Number(timestamp) * 1000);

    return new Date(
      encodedDate.getUTCFullYear(),
      encodedDate.getUTCMonth(),
      encodedDate.getUTCDate(),
      encodedDate.getUTCHours(),
      encodedDate.getUTCMinutes(),
      encodedDate.getUTCSeconds(),
      0
    );
  };

  const isPastTimestamp = (timestamp) =>
    timestampToLocalWallClock(timestamp + 3600).getTime() <= Date.now();

  const isPastSlot = (date, hour) =>
    isPastTimestamp(getSlotTimestamp(date, hour));

  const isPastDate = (date) => {
    const dayStart = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      0,
      0,
      0,
      0
    );

    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);

    return dayStart.getTime() < todayStart.getTime();
  };

  const isSlotSelected = (date, hour) =>
    availability.includes(getSlotTimestamp(date, hour));

  const normalizeSlots = (slots) =>
    Array.from(
      new Set(
        (Array.isArray(slots) ? slots : [])
          .map(Number)
          .filter(Number.isFinite)
      )
    ).sort((a, b) => a - b);

  const emitAvailability = (slots) => {
    handleSetAvailability?.(normalizeSlots(slots));
  };

  const toggleSingleSlot = (date, hour) => {
    if (isPastSlot(date, hour)) return;

    const timestamp = getSlotTimestamp(date, hour);
    const nextAvailability = availability.includes(timestamp)
      ? availability.filter((slot) => slot !== timestamp)
      : [...availability, timestamp];

    emitAvailability(nextAvailability);
  };

  const getSelectableSlotsForDay = (date) =>
    hours
      .map((hour) => getSlotTimestamp(date, hour))
      .filter(
        (timestamp) => !isPastTimestamp(timestamp)
      );

  const mobileDaySelectableSlots =
    getSelectableSlotsForDay(mobileDate);

  const isMobileDayFullySelected =
    mobileDaySelectableSlots.length > 0 &&
    mobileDaySelectableSlots.every((timestamp) =>
      availability.includes(timestamp)
    );

  const isMobileDateToday = (() => {
    const today = new Date();

    return (
      mobileDate.getFullYear() === today.getFullYear() &&
      mobileDate.getMonth() === today.getMonth() &&
      mobileDate.getDate() === today.getDate()
    );
  })();

  const toggleMobileWholeDay = () => {
    if (!mobileDaySelectableSlots.length) return;

    const nextAvailability = isMobileDayFullySelected
      ? availability.filter(
          (timestamp) =>
            !mobileDaySelectableSlots.includes(timestamp)
        )
      : [
          ...availability,
          ...mobileDaySelectableSlots,
        ];

    emitAvailability(nextAvailability);
  };

  const toggleAvailabilityRange = (start, end) => {
    if (!start || !end) return;

    const startTimestamp = getSlotTimestamp(start.date, start.hour);
    const endTimestamp = getSlotTimestamp(end.date, end.hour);
    const rangeStart = Math.min(startTimestamp, endTimestamp);
    const rangeEnd = Math.max(startTimestamp, endTimestamp);
    const range = [];

    for (
      let timestamp = rangeStart;
      timestamp <= rangeEnd;
      timestamp += 3600
    ) {
      if (!isPastTimestamp(timestamp)) {
        range.push(timestamp);
      }
    }

    if (!range.length) return;

    const shouldRemoveRange = range.every((timestamp) =>
      availability.includes(timestamp)
    );

    const nextAvailability = shouldRemoveRange
      ? availability.filter((timestamp) => !range.includes(timestamp))
      : [...availability, ...range];

    emitAvailability(nextAvailability);
  };

  const finishPointerSelection = () => {
    if (!isSelecting) return;

    toggleAvailabilityRange(selectionStart, selectionEnd);
    setIsSelecting(false);
    setSelectionStart(null);
    setSelectionEnd(null);
  };

  useEffect(() => {
    if (!isSelecting) return undefined;

    window.addEventListener("pointerup", finishPointerSelection);
    window.addEventListener("pointercancel", finishPointerSelection);

    return () => {
      window.removeEventListener("pointerup", finishPointerSelection);
      window.removeEventListener("pointercancel", finishPointerSelection);
    };
  }, [isSelecting, selectionStart, selectionEnd, availability]);

  const isInSelectionRange = (date, hour) => {
    if (!isSelecting || !selectionStart || !selectionEnd) return false;

    const timestamp = getSlotTimestamp(date, hour);
    const startTimestamp = getSlotTimestamp(
      selectionStart.date,
      selectionStart.hour
    );
    const endTimestamp = getSlotTimestamp(
      selectionEnd.date,
      selectionEnd.hour
    );

    return (
      timestamp >= Math.min(startTimestamp, endTimestamp) &&
      timestamp <= Math.max(startTimestamp, endTimestamp)
    );
  };

  const handleWeekChange = (direction) => {
    const nextDate = new Date(currentDate);
    nextDate.setDate(nextDate.getDate() + direction * 7);
    setCurrentDate(nextDate);

    const nextWeek = getWeekDates(nextDate);
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);

    const preferredDate = nextWeek.find(
      (date) => date >= todayStart
    );

    setMobileDate(preferredDate || nextWeek[0]);
  };

  const formatHour = (hour) =>
  `${String(hour).padStart(2, "0")}:00`;

  const weekRangeLabel = `${weekDates[0].toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  })} – ${weekDates[6].toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })}`;

  useEffect(() => {
    const today = new Date();
    const sameWeekDay = weekDates.find(
      (date) => date.toDateString() === today.toDateString()
    );
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);

    const firstAvailableDay = weekDates.find(
      (date) => date >= todayStart
    );

    setMobileDate((previousDate) => {
      const stillVisible = weekDates.some(
        (date) => date.toDateString() === previousDate.toDateString()
      );

      if (stillVisible) return previousDate;
      return sameWeekDay || firstAvailableDay || weekDates[0];
    });
  }, [currentDate]);

  useEffect(() => {
    if (desktopScrollRef.current) {
      desktopScrollRef.current.scrollTop = START_HOUR * ROW_HEIGHT;
    }

    if (mobileSlotsRef.current) {
      mobileSlotsRef.current.scrollTop =
        Math.floor(START_HOUR / 2) * 52;
    }
  }, [currentDate, ROW_HEIGHT]);

  return (
    <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm sm:rounded-3xl">
      <div className="border-b border-slate-200 bg-slate-50/70 p-3 sm:p-4">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-[#365561]/10 text-[#365561]">
                <i className="fa-regular fa-calendar" />
              </span>
              <div>
                <h4 className="text-sm font-semibold text-slate-900 sm:text-base">
                  Choose your availability
                </h4>
                <p className="text-[11px] leading-4 text-slate-500 sm:text-xs hidden md:block">
                  Click and drag to select a range.
                </p>
                <p className="text-[11px] leading-4 text-slate-500 sm:text-xs blobk md:hidden">
                  Tap a time slot, or use the checkbox to select the entire day.
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="flex h-[42px] items-center justify-between gap-1 rounded-[14px] border border-slate-200 bg-white px-1 shadow-sm sm:justify-start">
              <button
                type="button"
                onClick={() => handleWeekChange(-1)}
                className="inline-flex h-8 w-9 items-center justify-center rounded-[10px] text-slate-500 transition hover:bg-slate-100 focus:outline-none"
                aria-label="Previous week"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>

              <span className="min-w-0 flex-1 px-1 text-center text-xs font-semibold text-slate-700 sm:min-w-[145px]">
                {weekRangeLabel}
              </span>

              <button
                type="button"
                onClick={() => handleWeekChange(1)}
                className="inline-flex h-8 w-9 items-center justify-center rounded-[10px] text-slate-500 transition hover:bg-slate-100 focus:outline-none"
                aria-label="Next week"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>

            <div className="min-w-0 flex-1 sm:w-[230px] sm:flex-none">
              <TimezoneSelect
                value={timezone}
                onChange={(option) => setTimezone(option.value)}
                menuPortalTarget={
                  typeof document !== "undefined" ? document.body : null
                }
                styles={{
                  control: (base) => ({
                    ...base,
                    minHeight: 42,
                    height: 42,
                    borderRadius: 14,
                    borderColor: "#e2e8f0",
                    boxShadow: "none",
                    fontSize: embeddedWizardMode ? 12 : 13,
                  }),
                  valueContainer: (base) => ({
                    ...base,
                    height: 40,
                    paddingTop: 0,
                    paddingBottom: 0,
                  }),
                  indicatorsContainer: (base) => ({
                    ...base,
                    height: 40,
                  }),
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                }}
              />
            </div>

            {availability.length > 0 && (
              <button
                type="button"
                onClick={() => emitAvailability([])}
                className="inline-flex h-11 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 text-xs font-semibold text-slate-600 transition hover:bg-rose-50 hover:text-rose-600 focus:outline-none"
              >
                <i className="fa-solid fa-eraser" />
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Mobile touch-first calendar */}
      <div className="p-3 md:hidden">
        <div className="grid grid-cols-7 gap-1.5">
          {weekDates.map((date) => {
            const selected =
              date.toDateString() === mobileDate.toDateString();
            const pastDate = isPastDate(date);

            return (
              <button
                key={date.toISOString()}
                type="button"
                disabled={pastDate}
                onClick={() => setMobileDate(date)}
                className={[
                  "flex min-h-[58px] flex-col items-center justify-center rounded-2xl border px-1 transition focus:outline-none",
                  selected
                    ? "border-[#365561] bg-[#365561] text-white shadow-[0_8px_20px_rgba(54,85,97,0.20)]"
                    : pastDate
                      ? "cursor-not-allowed border-slate-100 bg-slate-50 text-slate-300"
                      : "border-slate-200 bg-white text-slate-600 active:scale-[0.98]",
                ].join(" ")}
              >
                <span className="text-[9px] font-semibold uppercase">
                  {date.toLocaleDateString("en-US", {
                    weekday: "narrow",
                  })}
                </span>
                <span className="mt-1 text-sm font-bold">
                  {date.getDate()}
                </span>
              </button>
            );
          })}
        </div>

        <div className="mt-3 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="truncate text-xs font-semibold text-slate-800">
              {mobileDate.toLocaleDateString("en-US", {
                weekday: "long",
                month: "long",
                day: "numeric",
              })}
            </p>
            <p className="mt-0.5 text-[11px] text-slate-500">
              Tap a time to add or remove it.
            </p>
          </div>

          <label
            className={[
              "inline-flex shrink-0 items-center gap-2 rounded-full border px-2.5 py-1.5 text-[10px] font-bold transition",
              mobileDaySelectableSlots.length > 0
                ? "cursor-pointer border-[#365561]/20 bg-[#365561]/5 text-[#365561]"
                : "cursor-not-allowed border-slate-200 bg-slate-50 text-slate-300",
            ].join(" ")}
          >
            <input
              type="checkbox"
              checked={isMobileDayFullySelected}
              disabled={
                mobileDaySelectableSlots.length === 0
              }
              onChange={toggleMobileWholeDay}
              className="h-4 w-4 cursor-pointer rounded border-slate-300 accent-[#365561] disabled:cursor-not-allowed"
              aria-label={
                isMobileDateToday
                  ? "Select the rest of today"
                  : "Select the whole day"
              }
            />

            <span className="whitespace-nowrap">
              {isMobileDateToday
                ? "Rest of day"
                : "Whole day"}
            </span>
          </label>
        </div>

        <div
          ref={mobileSlotsRef}
          className="mt-3 grid max-h-[390px] grid-cols-2 gap-2 overflow-y-auto overscroll-y-auto rounded-2xl bg-slate-50 p-2"
        >
          {hours.map((hour) => {
            const selected = isSlotSelected(mobileDate, hour);
            const past = isPastSlot(mobileDate, hour);
            const disabled = past;

            return (
              <button
                key={hour}
                type="button"
                disabled={disabled}
                onClick={() => toggleSingleSlot(mobileDate, hour)}
                className={[
                  "flex min-h-[46px] items-center justify-between rounded-xl border px-3 text-left transition focus:outline-none",
                  selected
                    ? "border-[#365561] bg-[#365561] text-white shadow-sm"
                    : disabled
                      ? "cursor-not-allowed border-slate-100 bg-white/60 text-slate-300"
                      : "border-slate-200 bg-white text-slate-700 active:scale-[0.98] active:bg-[#365561]/10",
                ].join(" ")}
              >
                <span className="text-xs font-semibold">
                  {formatHour(hour)}
                </span>
                {selected && (
                  <i className="fa-solid fa-check text-[10px]" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Desktop drag-select calendar */}
      <div className="hidden md:block">
        <div className="grid grid-cols-8 border-b border-slate-200 bg-white text-xs">
          <div className="border-r border-slate-200 p-2" />
          {weekDates.map((date) => {
            const today = new Date();

            const isToday =
              date.getFullYear() === today.getFullYear() &&
              date.getMonth() === today.getMonth() &&
              date.getDate() === today.getDate();

            const pastDate = isPastDate(date);

            return (
              <div
                key={date.toISOString()}
                className={[
                  "border-r border-slate-200 p-2 text-center last:border-r-0",
                  isToday
                    ? "border-[#365561] bg-[#365561] text-white shadow-[0_8px_20px_rgba(54,85,97,0.20)]"
                    : pastDate
                      ? "cursor-not-allowed border-slate-100 bg-slate-50 text-slate-300"
                      : "border-slate-200 bg-white text-slate-600 active:scale-[0.98]",
                ].join(" ")}
              >
                <p
                  className={[
                    "text-[10px] font-semibold uppercase tracking-wide",
                    isToday
                      ? "text-white/80"
                      : pastDate
                        ? "text-slate-300"
                        : "text-slate-400",
                  ].join(" ")}
                >
                  {date.toLocaleDateString("en-US", {
                    weekday: "short",
                  })}
                </p>

                <p
                  className={[
                    "mt-1 text-sm font-bold",
                    isToday
                      ? "text-white"
                      : pastDate
                        ? "text-slate-300"
                        : "text-slate-700",
                  ].join(" ")}
                >
                  {date.getDate()}
                </p>
              </div>
            );
          })}
        </div>

        <div
          ref={desktopScrollRef}
          className="overflow-y-auto overscroll-y-auto"
          style={{
            maxHeight: (END_HOUR - START_HOUR) * ROW_HEIGHT,
          }}
        >
          <div className="grid grid-cols-8 text-xs">
            {hours.map((hour) => (
              <React.Fragment key={hour}>
                <div
                  className="sticky left-0 z-10 relative border-r border-slate-200 bg-white"
                  style={{ height: ROW_HEIGHT }}
                >
                  {hour > 0 && (
                    <span className="absolute right-2 top-0 z-20 -translate-y-1/2 bg-white px-1 text-[10px] font-medium text-slate-400">
                      {formatHour(hour)}
                    </span>
                  )}
                </div>

                {weekDates.map((date) => {
                  const past = isPastSlot(date, hour);
                  const selected = isSlotSelected(date, hour);
                  const preview = isInSelectionRange(date, hour);
                  const disabled = past;

                  return (
                    <div
                      key={`${date.toISOString()}-${hour}`}
                      role="button"
                      tabIndex={disabled ? -1 : 0}
                      aria-disabled={disabled}
                      aria-pressed={selected}
                      onPointerDown={(event) => {
                        if (disabled) return;
                        event.preventDefault();
                        setIsSelecting(true);
                        setSelectionStart({ date, hour });
                        setSelectionEnd({ date, hour });
                      }}
                      onPointerEnter={() => {
                        if (isSelecting && !disabled) {
                          setSelectionEnd({ date, hour });
                        }
                      }}
                      onKeyDown={(event) => {
                        if (
                          !disabled &&
                          (event.key === "Enter" || event.key === " ")
                        ) {
                          event.preventDefault();
                          toggleSingleSlot(date, hour);
                        }
                      }}
                      className={[
                        "relative border-b border-r border-slate-200 last:border-r-0 transition",
                        disabled
                          ? "cursor-not-allowed bg-slate-50"
                          : "cursor-crosshair bg-white hover:bg-[#365561]/5",
                      ].join(" ")}
                      style={{ height: ROW_HEIGHT }}
                    >
                      {(selected || preview) && (
                        <div
                          className={[
                            "absolute inset-1 rounded-lg",
                            selected
                              ? "bg-[#365561] shadow-sm"
                              : "border-2 border-dashed border-[#365561]/50 bg-[#365561]/10",
                          ].join(" ")}
                        />
                      )}
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 border-t border-slate-200 bg-white px-3 py-2.5 text-[11px] text-slate-500 sm:px-4">
        <span>
          {availability.length} hour{availability.length === 1 ? "" : "s"} selected
        </span>
        <span className="hidden sm:inline">
          Past dates and completed times are unavailable
        </span>
      </div>
    </section>
  );
}
